import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useLocation } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  AlertColor,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useCGData } from '../hooks/useApiQueries';
import { useCGDocumentsStructured } from '../hooks/useApiQueries';
import theme from '../theme';
import { AgencyData } from '../types/cg';
import { formatUTCDateTime, removeSpaces } from '../utils';
import { isFrMobile, toFrPhoneInputValue, toStorage } from '../utils/frPhone';
import { useAuth } from './AuthContext';
import AdvantagesModal from './CGForm/AdvantagesModal';
import FilesOpener from './CGForm/FilesOpener';
import TaxModal from './CGForm/TaxModal';
import FirebaseUploadComponent from './CreateCotation/FirebaseUploadComponent';
import { ErrorMessageSnackbar } from './ErrorMessageSnackbar';
import FileCarouselDialog from './FileCarouselDialog';

const CGForm = () => {
  const { currentUser } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [hasSubmissionStarted, setHasSubmissionStarted] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('error');

  const [taxData, setTaxData] = useState(null);
  const [agencyData, setAgencyData] = useState<AgencyData | null>(null);
  const [registrationId, setRegistrationId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [birthDepartmentOptions, setBirthDepartmentOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedPostalCode, setSelectedPostalCode] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const registrationIdGetParameter = searchParams.get('registrationId');
  const shouldOpenExternalComments = searchParams.get('openExternalComments') === 'true';

  const { data: cgData, isLoading: isLoadingCGData } = registrationIdGetParameter
    ? useCGData(registrationIdGetParameter ?? '')
    : { data: null, isLoading: false };

  // Hook pour récupérer les documents structurés
  const { data: cgDocumentsStructured } = registrationIdGetParameter
    ? useCGDocumentsStructured(registrationIdGetParameter ?? '')
    : { data: null };

  const [sellerFilesUrls, setSellerFilesUrls] = useState<string[]>(
    cgData?.sellerFilesUrls ? cgData?.sellerFilesUrls.split(',').map((url: string) => url.trim()) : [],
  );

  // Nouveaux états pour les documents structurés du vendeur
  const [sellerCarteGriseUrls, setSellerCarteGriseUrls] = useState<string[]>([]);
  const [sellerCerfaCessionUrls, setSellerCerfaCessionUrls] = useState<string[]>([]);
  const [sellerCerfaDemandeImmatUrls, setSellerCerfaDemandeImmatUrls] = useState<string[]>([]);
  const [sellerMandatUrls, setSellerMandatUrls] = useState<string[]>([]);
  const [sellerAssuranceUrls, setSellerAssuranceUrls] = useState<string[]>([]);
  const [sellerControleTechniqueUrls, setSellerControleTechniqueUrls] = useState<string[]>([]);

  // Nouveaux états pour les documents structurés de l'acheteur
  const [buyerIdentityUrls, setBuyerIdentityUrls] = useState<string[]>([]);
  const [buyerLicenseUrls, setBuyerLicenseUrls] = useState<string[]>([]);

  // Nouveaux états pour les documents de résidence structurés
  const [buyerResidencyHostProofUrls, setBuyerResidencyHostProofUrls] = useState<string[]>([]);
  const [buyerResidencyHostIdentityUrls, setBuyerResidencyHostIdentityUrls] = useState<string[]>([]);
  const [buyerResidencyHonorAttestationUrls, setBuyerResidencyHonorAttestationUrls] = useState<string[]>([]);
  const [buyerResidencyGuestProofUrls, setBuyerResidencyGuestProofUrls] = useState<string[]>([]);

  const [buyerFilesUrls, setBuyerFilesUrls] = useState<string[]>(
    cgData?.buyerFilesUrls ? cgData?.buyerFilesUrls.split(',').map((url: string) => url.trim()) : [],
  );
  const [buyerResidencyFilesUrls, setBuyerResidencyFilesUrls] = useState<string[]>(
    cgData?.buyerResidencyFilesUrls ? cgData?.buyerResidencyFilesUrls.split(',').map((url: string) => url.trim()) : [],
  );
  const [coBuyerFilesUrls, setCoBuyerFilesUrls] = useState<string[]>(
    cgData?.coBuyerFilesUrls ? cgData?.coBuyerFilesUrls.split(',').map((url: string) => url.trim()) : [],
  );

  // États de validation pour chaque dropzone
  const [sellerCarteGriseValid, setSellerCarteGriseValid] = useState<boolean>(false);
  const [sellerCerfaCessionValid, setSellerCerfaCessionValid] = useState<boolean>(false);
  const [sellerCerfaDemandeImmatValid, setSellerCerfaDemandeImmatValid] = useState<boolean>(false);
  const [sellerMandatValid, setSellerMandatValid] = useState<boolean>(false);
  const [sellerAssuranceValid, setSellerAssuranceValid] = useState<boolean>(false);
  const [sellerControleTechniqueValid, setSellerControleTechniqueValid] = useState<boolean>(false);
  const [buyerIdentityValid, setBuyerIdentityValid] = useState<boolean>(false);
  const [buyerLicenseValid, setBuyerLicenseValid] = useState<boolean>(false);
  const [buyerResidencyValid, setBuyerResidencyValid] = useState<boolean>(false);
  const [coBuyerFilesValid, setCoBuyerFilesValid] = useState<boolean>(false);

  // États de validation pour les documents de résidence structurés
  const [buyerResidencyHostProofValid, setBuyerResidencyHostProofValid] = useState<boolean>(false);
  const [buyerResidencyHostIdentityValid, setBuyerResidencyHostIdentityValid] = useState<boolean>(false);
  const [buyerResidencyHonorAttestationValid, setBuyerResidencyHonorAttestationValid] = useState<boolean>(false);
  const [buyerResidencyGuestProofValid, setBuyerResidencyGuestProofValid] = useState<boolean>(false);

  const isReadOnly = registrationIdGetParameter != null;

  // Carousel state
  const [carouselOpen, setCarouselOpen] = useState<boolean>(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);

  const allCarouselFiles = useMemo(() => {
    if (!isReadOnly) return [];
    const urls: string[] = [];
    const addUrls = (source: string | string[] | undefined | null) => {
      if (!source) return;
      const arr = typeof source === 'string' ? source.split(',').map((u) => u.trim()) : source;
      arr.forEach((u) => {
        if (u && !urls.includes(u)) urls.push(u);
      });
    };

    // Documents structurés vendeur
    if (cgDocumentsStructured?.data?.sellerFiles) {
      const sf = cgDocumentsStructured.data.sellerFiles;
      addUrls(sf.carteGrise);
      addUrls(sf.cerfaCession);
      addUrls(sf.cerfaDemandeImmat);
      addUrls(sf.mandat);
      addUrls(sf.assurance);
      addUrls(sf.controleTechnique);
      addUrls(sf.legacy);
    }
    addUrls(cgData?.sellerFilesUrls);

    // Documents structurés acheteur
    if (cgDocumentsStructured?.data?.buyerFiles) {
      const bf = cgDocumentsStructured.data.buyerFiles;
      addUrls(bf.identity);
      addUrls(bf.license);
      addUrls(bf.legacy);
    }
    addUrls(cgData?.buyerFilesUrls);

    // Documents résidence
    if (cgDocumentsStructured?.data?.buyerResidencyFiles) {
      const rf = cgDocumentsStructured.data.buyerResidencyFiles;
      addUrls(rf.hostProofOfResidence);
      addUrls(rf.hostIdentity);
      addUrls(rf.honorAttestation);
      addUrls(rf.guestProofOfResidence);
      addUrls(rf.legacy);
    }
    addUrls(cgData?.buyerResidencyFilesUrls);

    // Co-acheteur
    addUrls(cgData?.coBuyerFilesUrls);

    // Commentaires
    addUrls(cgData?.commentFilesUrls);
    addUrls(cgData?.internalCommentFilesUrls);

    return urls;
  }, [cgDocumentsStructured, cgData, isReadOnly]);

  const handleCarouselOpen = (url: string) => {
    const index = allCarouselFiles.indexOf(url);
    setCarouselIndex(index >= 0 ? index : 0);
    setCarouselOpen(true);
  };

  const openSnackbar = (message: string, severity: AlertColor) => {
    setSnackbarMessage(message);
    setSeverity(severity);
    setSnackbarOpen(true);
  };

  const formMethods = useForm({
    mode: 'all',
    defaultValues: {
      // Identifiants et informations de base du véhicule
      regPlate: '',
      vin: '',
      numFormule: '',
      purchaseDateTime: '',

      // Informations sur les fichiers
      sellerFilesUrls: [],
      buyerFilesUrls: [],
      buyerResidencyFilesUrls: [],
      coBuyerFilesUrls: [],

      // Détails de l'ancien propriétaire
      sellerClientType: 'person',
      sellerGender: cgData?.sellerGender ?? '',
      sellerFirstName: '',
      sellerLastName: '',
      sellerUsualName: '',
      sellerSiret: '',
      sellerCompanyName: '',

      // Détails du nouveau propriétaire
      buyerClientType: 'person',
      buyerGender: cgData?.buyerGender ?? '',
      buyerFirstName: '',
      buyerLastName: '',
      buyerUsualName: '',
      buyerSiret: '',
      buyerCompanyName: '',

      // Adresse du nouveau propriétaire
      buyerAddressNumber: '',
      buyerAddressExtension: '',
      buyerAddressType: '',
      buyerAddressName: '',
      buyerAddressPlace: '',
      buyerAddressStairs: '',
      buyerAddressComp: '',
      buyerAddressPostalBox: '',
      buyerCity: '',
      buyerPostalCode: '',
      buyerCountry: '',

      // Informations supplémentaires pour les particuliers
      buyerResidenceType: 'owner',
      buyerDateOfBirth: '',
      buyerPlaceOfBirth: '',
      buyerCountryOfBirth: 'FRANCE',
      buyerDepartmentOfBirth: '',
      buyerPhone: '',
      buyerDocumentType: '', // Initialisé sans sélection car le type de document est conditionnel

      // Informations sur le co-titulaire
      cobuyer: 'no',
      coBuyerClientType: 'person',
      coBuyerGender: cgData?.coBuyerGender ?? '',
      coBuyerFirstName: '',
      coBuyerLastName: '',
      coBuyerUsualName: '',
      coBuyerCompanyName: '',
      coBuyerSiret: '',
    },
  });

  const { control, formState, reset, trigger, watch, setValue, setError, clearErrors } = formMethods;
  const { errors, isValid } = formState;

  const sellerClientType = watch('sellerClientType');
  const buyerClientType = watch('buyerClientType');
  const buyerResidenceType = watch('buyerResidenceType');
  const cobuyer = watch('cobuyer');
  const coBuyerClientType = watch('coBuyerClientType');
  const regPlateValue = watch('regPlate');
  const vinValue = watch('vin');
  const numFormuleValue = watch('numFormule');
  const buyerPostalCode = watch('buyerPostalCode');
  const buyerCountryOfBirth = watch('buyerCountryOfBirth');
  const isFrance = buyerCountryOfBirth === 'FRANCE';

  // Logique de validation pour les documents de résidence (utilisée dans plusieurs endroits)
  const buyerResidencyValidForStructured =
    buyerResidenceType === 'guest'
      ? buyerResidencyHostProofValid &&
        buyerResidencyHostIdentityValid &&
        buyerResidencyHonorAttestationValid &&
        buyerResidencyGuestProofValid
      : buyerResidencyValid;

  const validationRules = {
    regPlate: {
      required: 'Ce champ est requis',
      pattern: {
        value: /^(?:[A-Z]{2}-\d{3}-[A-Z]{2})|(?:\d{3} [A-Z]{3} \d{2})$/,
        message:
          "Le format de la plaque n'est pas valide. S'il s'agit d'une ancienne plaque, veuillez nous envoyer le dossier à <a style='color:black' href='mailto:skipcarcg@gmail.com' target='_blank'>skipcarcg@gmail.com</a>",
      },
      minLength: {
        value: 7,
        message: "L'immatriculation doit contenir au moins 7 caractères hors tirets et espaces",
      },
      validate: (value: string) => {
        const cleanedValue = value.replace(/[-\s]/g, ''); // Enlève les tirets et les espaces
        return (
          cleanedValue.length >= 7 || "L'immatriculation doit contenir au moins 7 caractères hors tirets et espaces"
        );
      },
    },
    vin: { required: 'Ce champ est requis' },
    numFormule: { required: 'Ce champ est requis' },

    sellerFirstName: { required: 'Ce champ est requis' },
    sellerLastName: { required: 'Ce champ est requis' },
    buyerFirstName: { required: 'Ce champ est requis' },
    buyerLastName: { required: 'Ce champ est requis' },
    coBuyerFirstName: { required: 'Ce champ est requis' },
    coBuyerLastName: { required: 'Ce champ est requis' },
    sellerGender: { required: 'Ce champ est requis' },
    buyerGender: { required: 'Ce champ est requis' },
    coBuyerGender: { required: 'Ce champ est requis' },

    addressNumber: {
      validate: (value: string) => {
        const addressType = watch('buyerAddressType');
        if (addressType !== 'ldit' && !value) {
          return 'Ce champ est requis';
        }
        return true;
      },
    },
    addressType: { required: 'Ce champ est requis' },
    addressName: { required: 'Ce champ est requis' },
    city: { required: 'Ce champ est requis' },
    postalCode: { required: 'Ce champ est requis' },
    country: { required: 'Ce champ est requis' },

    sellerSiret: { required: 'Ce champ est requis' },
    sellerCompanyName: { required: 'Ce champ est requis' },
    buyerSiret: { required: 'Ce champ est requis' },
    buyerCompanyName: { required: 'Ce champ est requis' },
    coBuyerSiret: { required: 'Ce champ est requis' },
    coBuyerCompanyName: { required: 'Ce champ est requis' },

    buyerPlaceOfBirth: { required: 'Ce champ est requis' },
    purchaseDateTime: {
      required: 'Ce champ est requis',
      validate: (value: string) => {
        if (!value) return 'Ce champ est requis';

        // Vérifier le format de la date
        const date = new Date(value);
        if (isNaN(date.getTime())) return 'Format de date invalide';

        // Vérifier que l'année est raisonnable (entre 2000 et année courante + 1)
        const year = date.getFullYear();
        const currentYear = new Date().getFullYear();
        if (year < 2000 || year > currentYear + 1) {
          return "L'année ne semble pas être au bon format";
        }

        return true;
      },
    },
    buyerDateOfBirth: {
      required: 'Ce champ est requis',
      validate: (value: string) => {
        if (!value) return 'Ce champ est requis';

        const date = new Date(value);
        if (isNaN(date.getTime())) return 'Format de date invalide';

        // Vérifier que la personne a au moins 17 ans
        const today = new Date();
        const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
        const maxDate = new Date(today.getFullYear() - 17, today.getMonth(), today.getDate());

        if (date > maxDate) return "L'acheteur doit avoir au moins 17 ans";
        if (date < minDate) return 'La date de naissance semble incorrecte';

        return true;
      },
    },
  };

  // Nouvelles fonctions pour les documents structurés du vendeur
  const sellerCarteGriseUploadComplete = (url: string) => {
    setSellerCarteGriseUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const sellerCarteGriseDeleteComplete = (url: string) => {
    setSellerCarteGriseUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const sellerCerfaCessionUploadComplete = (url: string) => {
    setSellerCerfaCessionUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const sellerCerfaCessionDeleteComplete = (url: string) => {
    setSellerCerfaCessionUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const sellerCerfaDemandeImmatUploadComplete = (url: string) => {
    setSellerCerfaDemandeImmatUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const sellerCerfaDemandeImmatDeleteComplete = (url: string) => {
    setSellerCerfaDemandeImmatUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const sellerMandatUploadComplete = (url: string) => {
    setSellerMandatUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const sellerMandatDeleteComplete = (url: string) => {
    setSellerMandatUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const sellerAssuranceUploadComplete = (url: string) => {
    setSellerAssuranceUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const sellerAssuranceDeleteComplete = (url: string) => {
    setSellerAssuranceUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const sellerControleTechniqueUploadComplete = (url: string) => {
    setSellerControleTechniqueUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const sellerControleTechniqueDeleteComplete = (url: string) => {
    setSellerControleTechniqueUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  // Nouvelles fonctions pour les documents structurés de l'acheteur
  const buyerIdentityUploadComplete = (url: string) => {
    setBuyerIdentityUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const buyerIdentityDeleteComplete = (url: string) => {
    setBuyerIdentityUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const buyerLicenseUploadComplete = (url: string) => {
    setBuyerLicenseUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const buyerLicenseDeleteComplete = (url: string) => {
    setBuyerLicenseUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const coBuyerFilesUploadComplete = (url: string) => {
    setCoBuyerFilesUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const coBuyerFilesDeleteComplete = (url: string) => {
    const updatedUrls = coBuyerFilesUrls.filter((u) => u !== url);
    setCoBuyerFilesUrls(updatedUrls);
  };

  const buyerResidencyFilesUploadComplete = async (url: string) => {
    setBuyerResidencyFilesUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const buyerResidencyFilesDeleteComplete = (url: string) => {
    const updatedUrls = buyerResidencyFilesUrls.filter((u) => u !== url);
    setBuyerResidencyFilesUrls(updatedUrls);
  };

  // Callbacks de validation pour chaque dropzone
  const handleSellerCarteGriseValidation = (isValid: boolean) => {
    setSellerCarteGriseValid(isValid);
  };

  const handleSellerCerfaCessionValidation = (isValid: boolean) => {
    setSellerCerfaCessionValid(isValid);
  };

  const handleSellerCerfaDemandeImmatValidation = (isValid: boolean) => {
    setSellerCerfaDemandeImmatValid(isValid);
  };

  const handleSellerMandatValidation = (isValid: boolean) => {
    setSellerMandatValid(isValid);
  };

  const handleSellerAssuranceValidation = (isValid: boolean) => {
    setSellerAssuranceValid(isValid);
  };

  const handleSellerControleTechniqueValidation = (isValid: boolean) => {
    setSellerControleTechniqueValid(isValid);
  };

  const handleBuyerIdentityValidation = (isValid: boolean) => {
    setBuyerIdentityValid(isValid);
  };

  const handleBuyerLicenseValidation = (isValid: boolean) => {
    setBuyerLicenseValid(isValid);
  };

  const handleBuyerResidencyValidation = (isValid: boolean) => {
    setBuyerResidencyValid(isValid);
  };

  const handleCoBuyerFilesValidation = (isValid: boolean) => {
    setCoBuyerFilesValid(isValid);
  };

  // Nouvelles fonctions pour les documents de résidence structurés
  const buyerResidencyHostProofUploadComplete = (url: string) => {
    setBuyerResidencyHostProofUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const buyerResidencyHostProofDeleteComplete = (url: string) => {
    setBuyerResidencyHostProofUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const buyerResidencyHostIdentityUploadComplete = (url: string) => {
    setBuyerResidencyHostIdentityUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const buyerResidencyHostIdentityDeleteComplete = (url: string) => {
    setBuyerResidencyHostIdentityUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const buyerResidencyHonorAttestationUploadComplete = (url: string) => {
    setBuyerResidencyHonorAttestationUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const buyerResidencyHonorAttestationDeleteComplete = (url: string) => {
    setBuyerResidencyHonorAttestationUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  const buyerResidencyGuestProofUploadComplete = (url: string) => {
    setBuyerResidencyGuestProofUrls((updatedUrls) => [...updatedUrls, url]);
  };

  const buyerResidencyGuestProofDeleteComplete = (url: string) => {
    setBuyerResidencyGuestProofUrls((updatedUrls) => updatedUrls.filter((u) => u !== url));
  };

  // Callbacks de validation pour les documents de résidence structurés
  const handleBuyerResidencyHostProofValidation = (isValid: boolean) => {
    setBuyerResidencyHostProofValid(isValid);
  };

  const handleBuyerResidencyHostIdentityValidation = (isValid: boolean) => {
    setBuyerResidencyHostIdentityValid(isValid);
  };

  const handleBuyerResidencyHonorAttestationValidation = (isValid: boolean) => {
    setBuyerResidencyHonorAttestationValid(isValid);
  };

  const handleBuyerResidencyGuestProofValidation = (isValid: boolean) => {
    setBuyerResidencyGuestProofValid(isValid);
  };

  useEffect(() => {
    if (cgData) {
      console.log('🔍 [CGForm] cgData reçu:', cgData);
      console.log('🔍 [CGForm] internalCommentFilesUrls:', (cgData as any)?.internalCommentFilesUrls);
      console.log('🔍 [CGForm] Type de internalCommentFilesUrls:', typeof (cgData as any)?.internalCommentFilesUrls);

      setBuyerFilesUrls(
        cgData?.buyerFilesUrls ? cgData?.buyerFilesUrls.split(',').map((url: string) => url.trim()) : [],
      );
      setBuyerResidencyFilesUrls(
        cgData?.buyerResidencyFilesUrls
          ? cgData?.buyerResidencyFilesUrls.split(',').map((url: string) => url.trim())
          : [],
      );
      setCoBuyerFilesUrls(
        cgData?.coBuyerFilesUrls ? cgData?.coBuyerFilesUrls.split(',').map((url: string) => url.trim()) : [],
      );
      setSellerFilesUrls(
        cgData?.sellerFilesUrls ? cgData?.sellerFilesUrls.split(',').map((url: string) => url.trim()) : [],
      );
    }
  }, [cgData]);

  // Charger les documents structurés quand ils sont disponibles
  useEffect(() => {
    if (cgDocumentsStructured?.data) {
      const documents = cgDocumentsStructured.data;

      // Charger les documents structurés du vendeur
      if (documents.sellerFiles) {
        setSellerCarteGriseUrls(documents.sellerFiles.carteGrise || []);
        setSellerCerfaCessionUrls(documents.sellerFiles.cerfaCession || []);
        setSellerCerfaDemandeImmatUrls(documents.sellerFiles.cerfaDemandeImmat || []);
        setSellerMandatUrls(documents.sellerFiles.mandat || []);
        setSellerAssuranceUrls(documents.sellerFiles.assurance || []);
        setSellerControleTechniqueUrls(documents.sellerFiles.controleTechnique || []);
      }

      // Charger les documents structurés de l'acheteur
      if (documents.buyerFiles) {
        setBuyerIdentityUrls(documents.buyerFiles.identity || []);
        setBuyerLicenseUrls(documents.buyerFiles.license || []);
      }

      // Charger les documents de résidence structurés
      if (documents.buyerResidencyFiles) {
        setBuyerResidencyHostProofUrls(documents.buyerResidencyFiles.hostProofOfResidence || []);
        setBuyerResidencyHostIdentityUrls(documents.buyerResidencyFiles.hostIdentity || []);
        setBuyerResidencyHonorAttestationUrls(documents.buyerResidencyFiles.honorAttestation || []);
        setBuyerResidencyGuestProofUrls(documents.buyerResidencyFiles.guestProofOfResidence || []);
      }
    }
  }, [cgDocumentsStructured]);

  const handleConfirmClick = async () => {
    // Déclenche la validation du formulaire
    const formValid = await trigger();

    // Vérifie la validation de toutes les dropzones obligatoires
    const allDropzonesValid =
      sellerCarteGriseValid &&
      sellerCerfaCessionValid &&
      sellerCerfaDemandeImmatValid &&
      sellerMandatValid &&
      sellerAssuranceValid &&
      sellerControleTechniqueValid &&
      buyerIdentityValid &&
      buyerLicenseValid &&
      (buyerClientType === 'pro' || buyerResidencyValidForStructured) && // Justificatif de domicile seulement pour les particuliers
      (cobuyer === 'no' || coBuyerFilesValid); // Documents co-titulaire seulement si applicable

    // Ouvre la modale de confirmation si le formulaire est valide et que toutes les dropzones sont valides
    if (formValid && allDropzonesValid) {
      setHasSubmissionStarted(true); // Marquer qu'une soumission a commencé
      setIsConfirmModalOpen(true);
    } else {
      // Afficher un message d'erreur pour indiquer le problème
      openSnackbar('Tous les documents obligatoires doivent être uploadés. Veuillez vérifier le formulaire.', 'error');
    }
  };

  useEffect(() => {
    if (buyerCountryOfBirth === 'FRANCE') {
      trigger('buyerDepartmentOfBirth'); // Déclenchez la revalidation du champ si le pays est la France
    }
  }, [buyerCountryOfBirth, trigger]);

  useEffect(() => {
    // Validation pour les documents structurés du vendeur - toutes les dropzones sont obligatoires
    if (!sellerCarteGriseValid) {
      setError('sellerCarteGrise', {
        type: 'manual',
        message: 'La carte grise est obligatoire',
      });
    } else {
      clearErrors('sellerCarteGrise');
    }

    if (!sellerCerfaCessionValid) {
      setError('sellerCerfaCession', {
        type: 'manual',
        message: 'Le Cerfa de déclaration de cession est obligatoire',
      });
    } else {
      clearErrors('sellerCerfaCession');
    }

    if (!sellerCerfaDemandeImmatValid) {
      setError('sellerCerfaDemandeImmat', {
        type: 'manual',
        message: "Le Cerfa de demande d'immatriculation est obligatoire",
      });
    } else {
      clearErrors('sellerCerfaDemandeImmat');
    }

    if (!sellerMandatValid) {
      setError('sellerMandat', {
        type: 'manual',
        message: 'Le mandat est obligatoire',
      });
    } else {
      clearErrors('sellerMandat');
    }

    if (!sellerAssuranceValid) {
      setError('sellerAssurance', {
        type: 'manual',
        message: "L'attestation d'assurance est obligatoire",
      });
    } else {
      clearErrors('sellerAssurance');
    }

    if (!sellerControleTechniqueValid) {
      setError('sellerControleTechnique', {
        type: 'manual',
        message: 'Le contrôle technique est obligatoire',
      });
    } else {
      clearErrors('sellerControleTechnique');
    }

    // Validation pour les documents structurés de l'acheteur
    if (!buyerIdentityValid) {
      setError('buyerIdentity', {
        type: 'manual',
        message: "La pièce d'identité est obligatoire",
      });
    } else {
      clearErrors('buyerIdentity');
    }

    if (!buyerLicenseValid) {
      setError('buyerLicense', {
        type: 'manual',
        message: 'Le permis de conduire est obligatoire',
      });
    } else {
      clearErrors('buyerLicense');
    }

    // Validation pour le justificatif de domicile (si applicable)
    if (buyerClientType === 'person') {
      if (buyerResidenceType === 'guest') {
        // Validation pour les documents de résidence structurés (hébergé)
        if (!buyerResidencyHostProofValid) {
          setError('buyerResidencyHostProof', {
            type: 'manual',
            message: "Le justificatif de domicile de l'hébergeur est obligatoire",
          });
        } else {
          clearErrors('buyerResidencyHostProof');
        }

        if (!buyerResidencyHostIdentityValid) {
          setError('buyerResidencyHostIdentity', {
            type: 'manual',
            message: "La pièce d'identité de l'hébergeur est obligatoire",
          });
        } else {
          clearErrors('buyerResidencyHostIdentity');
        }

        if (!buyerResidencyHonorAttestationValid) {
          setError('buyerResidencyHonorAttestation', {
            type: 'manual',
            message: "L'attestation sur l'honneur est obligatoire",
          });
        } else {
          clearErrors('buyerResidencyHonorAttestation');
        }

        if (!buyerResidencyGuestProofValid) {
          setError('buyerResidencyGuestProof', {
            type: 'manual',
            message: 'Le justificatif de domicile est obligatoire',
          });
        } else {
          clearErrors('buyerResidencyGuestProof');
        }
      } else {
        // Validation pour le justificatif de domicile classique (titulaire)
        if (!buyerResidencyValid) {
          setError('buyerResidency', {
            type: 'manual',
            message: 'Le justificatif de domicile est obligatoire',
          });
        } else {
          clearErrors('buyerResidency');
        }
      }
    } else {
      clearErrors('buyerResidency');
      clearErrors('buyerResidencyHostProof');
      clearErrors('buyerResidencyHostIdentity');
      clearErrors('buyerResidencyHonorAttestation');
      clearErrors('buyerResidencyGuestProof');
    }

    // Validation pour les documents du co-titulaire (si applicable)
    if (cobuyer === 'yes' && !coBuyerFilesValid) {
      setError('coBuyerFiles', {
        type: 'manual',
        message: 'Les documents du co-titulaire sont obligatoires',
      });
    } else {
      clearErrors('coBuyerFiles');
    }

    // Validation pour 'gender'
    if (!watch('sellerGender')) {
      setError('sellerGender', {
        type: 'manual',
        message: 'Ce champ est requis',
      });
    } else {
      clearErrors('sellerGender');
    }

    if (!watch('buyerGender')) {
      setError('buyerGender', {
        type: 'manual',
        message: 'Ce champ est requis',
      });
    } else {
      clearErrors('buyerGender');
    }

    // Validation pour 'coBuyerGender' seulement si 'cobuyer' est 'yes'
    if (cobuyer === 'yes' && !watch('coBuyerGender')) {
      setError('coBuyerGender', {
        type: 'manual',
        message: 'Ce champ est requis',
      });
    } else {
      clearErrors('coBuyerGender');
    }

    // Déclenche la revalidation du formulaire pour mettre à jour l'état du bouton de soumission
    trigger();
  }, [
    sellerCarteGriseValid,
    sellerCerfaCessionValid,
    sellerCerfaDemandeImmatValid,
    sellerMandatValid,
    sellerAssuranceValid,
    sellerControleTechniqueValid,
    buyerIdentityValid,
    buyerLicenseValid,
    buyerResidencyValid,
    buyerResidencyHostProofValid,
    buyerResidencyHostIdentityValid,
    buyerResidencyHonorAttestationValid,
    buyerResidencyGuestProofValid,
    coBuyerFilesValid,
    buyerClientType,
    buyerResidenceType,
    cobuyer,
    setError,
    clearErrors,
    trigger,
  ]);

  useEffect(() => {
    if (coBuyerClientType === 'person') {
      formMethods.resetField('coBuyerCompanyName');
      formMethods.resetField('coBuyerSiret');
    } else if (coBuyerClientType === 'pro') {
      formMethods.resetField('coBuyerGender');
    }
  }, [coBuyerClientType]);

  // Afficher ou masquer les champs du co-titulaire basé sur le choix de l'utilisateur
  useEffect(() => {
    setValue('cobuyer', cgData && cgData.CoBuyerType ? 'yes' : 'no');
  }, [cgData, setValue]);

  useEffect(() => {
    if (cgData) {
      reset({
        regPlate: cgData.RegPlate ?? '',
        vin: cgData.VIN ?? '',
        numFormule: cgData.NumFormule ?? '',
        purchaseDateTime: formatUTCDateTime(cgData.PurchaseDateTime) ?? '',
        buyerClientType: cgData.BuyerType ?? 'person',
        buyerFirstName: cgData.BuyerFirstName ?? '',
        buyerLastName: cgData.BuyerLastName ?? '',
        buyerUsualName: cgData.BuyerUsualName ?? '',
        buyerGender: cgData.BuyerGender ?? '',
        buyerDateOfBirth: cgData.BuyerDateOfBirth ? cgData.BuyerDateOfBirth.substring(0, 10) : '',
        buyerPlaceOfBirth: cgData.BuyerPlaceOfBirth ?? '',
        buyerCountryOfBirth: cgData.BuyerCountryOfBirth ?? '',
        buyerDepartmentOfBirth: cgData.BuyerDepartmentOfBirth ?? '',
        buyerDocumentType: cgData.BuyerDocumentType ?? '',
        buyerCompanyName: cgData.BuyerCompanyName ?? '',
        buyerSiret: cgData.BuyerSIRET ?? '',
        buyerAddressNumber: cgData.BuyerAddressNumber ?? '',
        buyerAddressExtension: cgData.BuyerAddressExtension ?? '',
        buyerAddressType: cgData.BuyerAddressType ?? '',
        buyerAddressName: cgData.BuyerAddressName ?? '',
        buyerAddressPlace: cgData.BuyerAddressPlace ?? '',
        buyerAddressStairs: cgData.BuyerAddressStairs ?? '',
        buyerAddressComp: cgData.BuyerAddressComplement ?? '',
        buyerAddressPostalBox: cgData.BuyerAddressPostalBox ?? '',
        buyerCity: cgData.BuyerCity ?? '',
        buyerPostalCode: cgData.BuyerPostalCode ?? '',
        buyerCountry: cgData.BuyerCountry ?? '',
        buyerPhone: toFrPhoneInputValue(cgData.BuyerPhone),
        buyerResidenceType: cgData.BuyerResidenceType ?? 'owner',
        sellerClientType: cgData.SellerType ?? 'person',
        sellerFirstName: cgData.SellerFirstName ?? '',
        sellerLastName: cgData.SellerLastName ?? '',
        sellerUsualName: cgData.SellerUsualName ?? '',
        sellerGender: cgData.SellerGender ?? 'mr',
        sellerSiret: cgData.SellerSIRET ?? '',
        sellerCompanyName: cgData.SellerCompanyName ?? '',
        cobuyer: cgData.CoBuyerType ? 'yes' : 'no',
        coBuyerClientType: cgData.CoBuyerType ?? 'person',
        coBuyerGender: cgData.CoBuyerGender ?? 'mr',
        coBuyerFirstName: cgData.CoBuyerFirstName ?? '',
        coBuyerLastName: cgData.CoBuyerLastName ?? '',
        coBuyerUsualName: cgData.CoBuyerUsualName ?? '',
        coBuyerCompanyName: cgData.CoBuyerCompanyName ?? '',
        coBuyerSiret: cgData.CoBuyerSIRET ?? '',
        sellerFilesUrls: cgData.SellerFiles ? cgData.SellerFiles.split(',') : [],
        buyerFilesUrls: cgData.BuyerFiles ? cgData.BuyerFiles.split(',') : [],
        buyerResidencyFilesUrls: cgData.BuyerResidencyFiles ? cgData.BuyerResidencyFiles.split(',') : [],
        coBuyerFilesUrls: cgData.CoBuyerFiles ? cgData.CoBuyerFiles.split(',') : [],
      });
    }
  }, [cgData, reset]);

  useEffect(() => {
    if (!cgData) {
      setValue('buyerDateOfBirth', ''); // Force à être vide après le rendu
      setValue('purchaseDateTime', ''); // Force à être vide après le rendu
    }
  }, []);

  const handlePostalCodeChange = async (event: any, value: string) => {
    if (value && value.length === 5) {
      try {
        const response = await makeAuthenticatedApiCall('get', `/api/geo/pc/?searchParam=${value}`);
        if (response.data && response.data.length > 0) {
          const filteredCities = response.data.map((item: { name: any; postcode: any }) => ({
            label: item.name,
            value: item.postcode,
          }));
          setCityOptions(filteredCities);
        } else {
          setCityOptions([]);
        }
      } catch (error) {
        console.error('Error fetching city data:', error);
        setCityOptions([]);
      }
    } else {
      setCityOptions([]);
    }
  };

  const handleBirthCityChange = async (event: any, value: string) => {
    if (value && value.length > 3) {
      try {
        const response = await makeAuthenticatedApiCall(
          'get',
          `/api/geo/cities/?searchParam=${value}&searchType=municipality`,
        );
        if (response.data && response.data.length > 0) {
          const birthDepartmentOptions = response.data.map((item: { name: any; postcode: any }) => ({
            label: item.name,
            value: item.postcode,
          }));
          setBirthDepartmentOptions(birthDepartmentOptions);
        } else {
          setBirthDepartmentOptions([]);
        }
      } catch (error) {
        console.error('Error fetching city data:', error);
        setBirthDepartmentOptions([]);
      }
    } else {
      setBirthDepartmentOptions([]);
    }
  };

  const handleFinalConfirm = async () => {
    // Protection contre les soumissions multiples
    if (isLoading) {
      console.log('Soumission déjà en cours, ignorée');
      return;
    }

    setIsLoading(true);
    setProgress(0);
    setCurrentStep('Préparation de la demande');
    const formData = formMethods.getValues(); // Récupérer les données du formulaire actuel
    console.log('formData', formData);

    try {
      await handleSave(formData);
      // La modale se fermera automatiquement quand TaxModal s'ouvre
    } catch (error: any) {
      console.error(error);
      openSnackbar(
        'Il y a eu une erreur au moment de la validation. Veuillez re-controler les dates et re-soumettre',
        'error',
      );
      setIsLoading(false);
      setProgress(0);
      setCurrentStep('');
    }
  };

  const handleSave = async (formData: any /* FormDataPerson | FormDataPro */) => {
    let payload, agencyInfo;

    try {
      setCurrentStep('Récupération des informations utilisateur');
      setProgress(10);

      const buyerResidencyStructured = {
        hostProofOfResidence: buyerResidencyHostProofUrls,
        hostIdentity: buyerResidencyHostIdentityUrls,
        honorAttestation: buyerResidencyHonorAttestationUrls,
        guestProofOfResidence: buyerResidencyGuestProofUrls,
      };

      payload = {
        ...formData,
        // Canonique en base : 0XXXXXXXXX (le widget travaille en 33…).
        buyerPhone: toStorage(formData.buyerPhone),
        sellerFilesUrls: JSON.stringify(sellerFilesUrls),
        buyerFilesUrls: JSON.stringify(buyerFilesUrls),
        buyerResidencyFilesUrls: JSON.stringify(buyerResidencyFilesUrls),
        coBuyerFilesUrls: JSON.stringify(coBuyerFilesUrls),
        // Nouveaux documents structurés du vendeur
        sellerFilesStructured: JSON.stringify({
          carteGrise: sellerCarteGriseUrls,
          cerfaCession: sellerCerfaCessionUrls,
          cerfaDemandeImmat: sellerCerfaDemandeImmatUrls,
          mandat: sellerMandatUrls,
          assurance: sellerAssuranceUrls,
          controleTechnique: sellerControleTechniqueUrls,
        }),
        // Nouveaux documents structurés de l'acheteur
        buyerFilesStructured: JSON.stringify({
          identity: buyerIdentityUrls,
          license: buyerLicenseUrls,
        }),
        // Nouveaux documents de résidence structurés
        buyerResidencyFilesStructured: JSON.stringify(buyerResidencyStructured),
        agencyID: currentUser?.data?.organization?.value ?? currentUser?.data?.data?.idPipedrive,
        personID: currentUser?.data?.id,
        agencyEmail:
          currentUser?.data?.organization_email || // Priorité : email de l'organisation depuis la BDD
          currentUser?.data?.primary_email || // Fallback : email principal de Pipedrive
          currentUser?.data?.email, // Fallback final : email de l'utilisateur
        agencyName: currentUser?.data?.organization?.name,
        agencyAddress: currentUser?.data?.organization?.address,
      };

      agencyInfo = {
        agencyID: currentUser?.data?.organization?.value ?? currentUser?.data?.data?.idPipedrive,
        personID: currentUser?.data?.id,
        agencyEmail:
          currentUser?.data?.organization_email || // Priorité : email de l'organisation depuis la BDD
          currentUser?.data?.primary_email || // Fallback : email principal de Pipedrive
          currentUser?.data?.email, // Fallback final : email de l'utilisateur
        agencyName: currentUser?.data?.organization?.name,
        agencyAddress: currentUser?.data?.organization?.address,
      };

      // Update the agency data in state
      setAgencyData(agencyInfo);
    } catch (error: any) {
      console.error(error);
      openSnackbar("Une erreur est survenue lors de la récupération des informations de l'utilisateur", 'error');
    }

    try {
      setCurrentStep('Validation des informations');
      setProgress(20);

      // Validation du code postal pour extraire le département
      if (!buyerPostalCode || buyerPostalCode.length < 2) {
        throw new Error('Le code postal doit contenir au moins 2 chiffres pour déterminer le département');
      }

      // Extraction du département en tenant compte des départements d'outre-mer
      let departmentValue: string;
      const VALID_DOM_DEPARTMENTS = ['971', '972', '973', '974', '975', '976'];

      // Si le code postal commence par 97, extraire 3 chiffres pour les DOM
      if (buyerPostalCode.startsWith('97')) {
        if (buyerPostalCode.length < 3) {
          throw new Error("Le code postal doit contenir au moins 3 chiffres pour les départements d'outre-mer");
        }
        departmentValue = buyerPostalCode.substring(0, 3);

        // Validation des départements d'outre-mer autorisés
        if (!VALID_DOM_DEPARTMENTS.includes(departmentValue)) {
          throw new Error(
            `Le département ${departmentValue} n'est pas pris en charge. Seuls les départements 971 (Guadeloupe), 972 (Martinique), 973 (Guyane), 974 (La Réunion), 975 (Saint-Pierre-et-Miquelon), et 976 (Mayotte) sont acceptés.`,
          );
        }
      } else {
        // Pour les départements métropolitains, extraire 2 chiffres
        departmentValue = buyerPostalCode.substring(0, 2);

        // Vérification que les 2 premiers caractères sont bien des chiffres
        if (!/^\d{2}$/.test(departmentValue)) {
          throw new Error('Le code postal doit commencer par 2 chiffres pour déterminer le département');
        }
      }

      if (!payload.purchaseDateTime || (payload.buyerClientType === 'person' && !payload.buyerDateOfBirth)) {
        openSnackbar(
          'Il arrive que les dates ne soient pas correctements prises en comptes avec Safari. Veuillez re-controler et re-soumettre',
          'error',
        );
        throw new Error(
          'Il arrive que les dates ne soient pas correctements prises en comptes avec Safari. Veuillez re-controler et re-soumettre',
        );
      }

      setCurrentStep('Enregistrement de la demande');
      setProgress(40);

      const responseSave = await makeAuthenticatedApiCall('post', '/api/cg', payload);

      setCurrentStep('Calcul des taxes');
      setProgress(70);

      const responseECG = await makeAuthenticatedApiCall('post', '/api/misiv/ecg', {
        regPlate: regPlateValue,
        department: departmentValue,
      });

      setCurrentStep('Finalisation');
      setProgress(100);

      openSnackbar('Les modifications ont bien été prises en compte', 'success');
      setTaxData(responseECG.data);
      setRegistrationId(responseSave.data.registrationId);
      setModalOpen(true);
      setIsConfirmModalOpen(false); // Fermer la modale de confirmation maintenant
    } catch (error: any) {
      console.error(error);
      // Vérifie si l'erreur provient d'une réponse HTTP, et utilisez le message d'erreur du serveur
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.error ||
            error.response.data.details ||
            error.response.data.message ||
            "Une erreur inattendue s'est produite. Veuillez vérifier à nouveau les champs, puis réessayer."
          : error.message ||
            "Une erreur inattendue s'est produite. Veuillez vérifier à nouveau les champs, puis réessayer.";
      openSnackbar(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour formater la plaque d'immatriculation avec des tirets
  const formatRegPlate = (value: string) => {
    // Retire tous les caractères non-alphanumériques et les tirets existants
    const alphanumericValue = value.replace(/[^A-Z0-9]/gi, '');
    // Ajoute un tiret après le 2ème caractère et après le 5ème caractère si le format correspond
    if (alphanumericValue.length > 2 && alphanumericValue.length <= 5) {
      return `${alphanumericValue.slice(0, 2)}-${alphanumericValue.slice(2)}`;
    } else if (alphanumericValue.length > 5) {
      return `${alphanumericValue.slice(0, 2)}-${alphanumericValue.slice(2, 5)}-${alphanumericValue.slice(5)}`;
    }

    return alphanumericValue.substring(0, 9);
  };

  const renderErrorMessage = (error: any) => {
    if (error?.type === 'pattern') {
      return (
        <span>
          Le format de la plaque n'est pas valide. S'il s'agit d'une ancienne plaque, veuillez utiliser le formulaire
          suivant :{' '}
          <a
            href="https://form.typeform.com/to/qkz4Zb0M"
            target="_blank"
            style={{ color: 'black' }}
            rel="noopener noreferrer"
          >
            https://form.typeform.com/to/qkz4Zb0M
          </a>
          .
        </span>
      );
    }
    return error?.message;
  };

  // Fonction de validation du numéro de téléphone (mobile FR 06/07, tous formats d'entrée).
  const isValidPhoneNumber = (phone: string): boolean => isFrMobile(phone);

  // Composant wrapper pour les champs véhicule avec tooltip optimisé
  const VehicleFieldWrapper = React.useMemo(
    () =>
      ({ children, disabled }: { children: React.ReactNode; disabled: boolean }) => {
        if (disabled) {
          return (
            <Tooltip title="Une première demande est en cours de soumission, vous devez rafraichir la page si vous voulez faire une demande de CG concernant un autre véhicule">
              <span>{children}</span>
            </Tooltip>
          );
        }
        return <>{children}</>;
      },
    [],
  );

  console.log(errors);
  return registrationIdGetParameter && isLoadingCGData ? (
    <CircularProgress sx={{ marginRight: 2 }} size={24} />
  ) : (
    <>
      <Container maxWidth={false}>
        <Box
          sx={{
            margin: { xs: '2em 1em', md: '2em 3em' },
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: registrationIdGetParameter ? 'none' : 'block',
          }}
        >
          <Typography variant="body1" align="center" gutterBottom>
            Comment faire votre demande de carte grise
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                secondary={
                  <span>
                    <strong>ÉTAPE 1 :</strong> Utilisez notre calculateur en ligne pour estimer le prix de votre
                    certificat d'immatriculation en remplissant simplement les champs requis.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                secondary={
                  <span>
                    <strong>ÉTAPE 2 :</strong> Après validation, consultez votre facture et procédez au paiement par
                    virement pour confirmer la transaction (si vous souhaitez passer par facturation quinzaine ou
                    mensuel, veuillez nous contacter via{' '}
                    <a href="mailto:skipcarcg@gmail.com" target="_blank" style={{ color: '#fdd15f' }}>
                      skipcarcg@gmail.com
                    </a>
                    ).
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                secondary={
                  <span>
                    <strong>ÉTAPE 3 :</strong> Une fois le paiement effectué, notre équipe enclenche le processus pour
                    traiter votre demande dans les meilleurs délais.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                secondary={
                  <span>
                    <strong>ÉTAPE 4 :</strong> Obtenez immédiatement votre Accusé d'Enregistrement de Changement de
                    Titulaire ou votre Certificat Provisoire d'Immatriculation pour que votre client puisse circuler
                    pendant 1 mois. Recevez ensuite votre carte grise définitive par courrier sécurisé de l'Imprimerie
                    Nationale.
                  </span>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                secondary={
                  <span>
                    <strong>ÉTAPE 5 :</strong> Suivez facilement toutes vos demandes à travers notre interface et
                    retrouvez vos récépissés et factures dans votre historique.
                  </span>
                }
              />
            </ListItem>
          </List>
          <AdvantagesModal />
        </Box>
        <Typography
          variant="h1"
          align="center"
          sx={{ color: '#EF8F35', margin: { xs: '0.5em auto 1em', md: '1em auto' } }}
        >
          Demande de carte grise
        </Typography>
        <Paper sx={{ marginTop: 1, marginBottom: { xs: 4, md: 0 } }} elevation={0}>
          <Box sx={{ p: { xs: 0, md: 4 } }}>
            <FormProvider {...formMethods}>
              <form>
                <Grid container>
                  <FormLabel component="legend">Véhicule</FormLabel>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <VehicleFieldWrapper disabled={hasSubmissionStarted && !isReadOnly}>
                        <Controller
                          name="purchaseDateTime"
                          control={control}
                          rules={validationRules.purchaseDateTime}
                          defaultValue=""
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              disabled={isReadOnly || hasSubmissionStarted}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              margin="dense"
                              className="inputElement"
                              id="purchaseDateTime"
                              label="Date et heure de la vente"
                              type="datetime-local"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              // Ajouter des contraintes min/max
                              inputProps={{
                                min: '2000-01-01T00:00',
                                max: '3000-01-01T00:00',
                              }}
                            />
                          )}
                        />
                      </VehicleFieldWrapper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <VehicleFieldWrapper disabled={hasSubmissionStarted && !isReadOnly}>
                        <Controller
                          name="regPlate"
                          control={control}
                          rules={validationRules.regPlate}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              disabled={isReadOnly || hasSubmissionStarted}
                              className="inputElement"
                              {...field}
                              inputProps={{ maxLength: 9 }} // Limite à 9 caractères
                              value={regPlateValue}
                              helperText={renderErrorMessage(errors.regPlate)}
                              onChange={(e) => {
                                // Met à jour la valeur du champ avec la valeur formatée et limitée
                                const upperCaseValue = e.target.value.toUpperCase();
                                field.onChange(formatRegPlate(upperCaseValue));
                              }}
                              error={!!errors?.regPlate?.message}
                              margin="dense"
                              label="Immatriculation"
                              type="text"
                              fullWidth
                            />
                          )}
                        />
                      </VehicleFieldWrapper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <VehicleFieldWrapper disabled={hasSubmissionStarted && !isReadOnly}>
                        <Controller
                          name="vin"
                          control={control}
                          rules={validationRules.vin}
                          defaultValue={''}
                          render={({ field }) => (
                            <TextField
                              disabled={isReadOnly || hasSubmissionStarted}
                              {...field}
                              error={!!errors.vin}
                              margin="dense"
                              className="inputElement"
                              id="vin"
                              label="Numéro VIN"
                              type="text"
                              fullWidth
                              inputProps={{ maxLength: 17 }}
                              onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                            />
                          )}
                        />
                      </VehicleFieldWrapper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <VehicleFieldWrapper disabled={hasSubmissionStarted && !isReadOnly}>
                        <Controller
                          name="numFormule"
                          control={control}
                          rules={validationRules.numFormule}
                          defaultValue={''}
                          render={({ field }) => (
                            <TextField
                              disabled={isReadOnly || hasSubmissionStarted}
                              {...field}
                              error={!!errors.numFormule}
                              margin="dense"
                              className="inputElement"
                              id="numFormule"
                              label="Numéro de Formule"
                              type="text"
                              fullWidth
                              inputProps={{ maxLength: 11 }}
                              onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                            />
                          )}
                        />
                      </VehicleFieldWrapper>
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Typography variant="body1" align="center">
                        Documents du vendeur - Veuillez organiser vos documents par type / Aucun dossier ne doit être
                        soumis si il n'est pas complet
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ fontSize: '16px' }}>
                        Les dossiers transmis de manière incomplète ou fragmentée engendrent des délais de traitement
                        plus longs, ne sont pas considérés comme prioritaires, et si ils se répètent ne pourront pas
                        conserver les conditions tarifaires actuelles, en raison de leur complexité accrue.
                      </Typography>

                      {/* Vérifier si on a des documents structurés ou legacy */}
                      {cgData ? (
                        // Mode lecture seule - vérifier le type de données
                        (() => {
                          const hasStructuredDocuments =
                            cgDocumentsStructured?.data?.sellerFiles &&
                            (cgDocumentsStructured.data.sellerFiles.carteGrise?.length > 0 ||
                              cgDocumentsStructured.data.sellerFiles.cerfaCession?.length > 0 ||
                              cgDocumentsStructured.data.sellerFiles.cerfaDemandeImmat?.length > 0 ||
                              cgDocumentsStructured.data.sellerFiles.mandat?.length > 0 ||
                              cgDocumentsStructured.data.sellerFiles.assurance?.length > 0 ||
                              cgDocumentsStructured.data.sellerFiles.controleTechnique?.length > 0);

                          const hasLegacyDocuments =
                            cgDocumentsStructured?.data?.sellerFiles?.legacy?.length > 0 || cgData.sellerFilesUrls;

                          // Si on a des documents structurés ET pas de documents legacy, afficher les dropzones séparées
                          if (hasStructuredDocuments && !hasLegacyDocuments) {
                            // Afficher les 6 dropzones séparées avec documents structurés
                            return (
                              <Grid container spacing={2} sx={{ mt: 2 }}>
                                {/* Carte grise */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" component="div" textAlign={'center'}>
                                    1 - Carte grise avec la mention "cédée le ../../.." et la signature
                                  </Typography>
                                  {cgDocumentsStructured?.data?.sellerFiles?.carteGrise?.length > 0 && (
                                    <FilesOpener
                                      onFileClick={handleCarouselOpen}
                                      FilesUrls={cgDocumentsStructured.data.sellerFiles.carteGrise.join(',')}
                                    />
                                  )}
                                </Grid>

                                {/* Cerfa de déclaration de cession */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" component="div" textAlign={'center'}>
                                    2 - Cerfa de déclaration de cession{' '}
                                    <a
                                      href="https://immatriculation.ants.gouv.fr/files/1e4341b6-4695-4f74-a51a-5ef962fba392/ef4ec059-07a7-4cc7-804f-e16944afeb5e/87e1c701-91dd-49de-86ed-52b7cb406dd0/cerfa_15776-01.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#fdd15f', textDecoration: 'underline' }}
                                    >
                                      (Cerfa 15776*01)
                                    </a>
                                  </Typography>
                                  {cgDocumentsStructured?.data?.sellerFiles?.cerfaCession?.length > 0 && (
                                    <FilesOpener
                                      onFileClick={handleCarouselOpen}
                                      FilesUrls={cgDocumentsStructured.data.sellerFiles.cerfaCession.join(',')}
                                    />
                                  )}
                                </Grid>

                                {/* Cerfa de demande d'immatriculation */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" component="div" textAlign={'center'}>
                                    3 - Cerfa de demande d'immatriculation{' '}
                                    <a
                                      href="https://immatriculation.ants.gouv.fr/files/1e4341b6-4695-4f74-a51a-5ef962fba392/ef4ec059-07a7-4cc7-804f-e16944afeb5e/0b6477d5-86c9-43bb-bf65-08ade3d1ca37/cerfa_13750-05.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#fdd15f', textDecoration: 'underline' }}
                                    >
                                      (Cerfa 13750*05)
                                    </a>
                                  </Typography>
                                  {cgDocumentsStructured?.data?.sellerFiles?.cerfaDemandeImmat?.length > 0 && (
                                    <FilesOpener
                                      onFileClick={handleCarouselOpen}
                                      FilesUrls={cgDocumentsStructured.data.sellerFiles.cerfaDemandeImmat.join(',')}
                                    />
                                  )}
                                </Grid>

                                {/* Mandat à un professionnel */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" component="div" textAlign={'center'}>
                                    4 - Mandat à un professionnel{' '}
                                    <a
                                      href="https://www.service-public.fr/simulateur/calcul/13757"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#fdd15f', textDecoration: 'underline' }}
                                    >
                                      (Cerfa 13757*03)
                                    </a>
                                  </Typography>
                                  {cgDocumentsStructured?.data?.sellerFiles?.mandat?.length > 0 && (
                                    <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgDocumentsStructured.data.sellerFiles.mandat.join(',')} />
                                  )}
                                </Grid>

                                {/* Attestation d'assurance */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" component="div" textAlign={'center'}>
                                    5 - Attestation d'assurance
                                  </Typography>
                                  {cgDocumentsStructured?.data?.sellerFiles?.assurance?.length > 0 && (
                                    <FilesOpener
                                      onFileClick={handleCarouselOpen}
                                      FilesUrls={cgDocumentsStructured.data.sellerFiles.assurance.join(',')}
                                    />
                                  )}
                                </Grid>

                                {/* Contrôle technique */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" component="div" textAlign={'center'}>
                                    6 - Contrôle technique
                                  </Typography>
                                  {cgDocumentsStructured?.data?.sellerFiles?.controleTechnique?.length > 0 && (
                                    <FilesOpener
                                      onFileClick={handleCarouselOpen}
                                      FilesUrls={cgDocumentsStructured.data.sellerFiles.controleTechnique.join(',')}
                                    />
                                  )}
                                </Grid>
                              </Grid>
                            );
                          } else {
                            // Afficher une seule section avec tous les fichiers legacy
                            return (
                              <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={12}>
                                  {cgDocumentsStructured?.data?.sellerFiles?.legacy?.length > 0 ? (
                                    <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgDocumentsStructured.data.sellerFiles.legacy.join(',')} />
                                  ) : (
                                    cgData.sellerFilesUrls && <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgData.sellerFilesUrls} />
                                  )}
                                </Grid>
                              </Grid>
                            );
                          }
                        })()
                      ) : (
                        // Mode création - afficher les 6 dropzones
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                          {/* Carte grise */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" component="div" textAlign={'center'}>
                              1 - Carte grise avec la mention "cédée le ../../.." et la signature
                            </Typography>
                            <FirebaseUploadComponent
                              onUploadComplete={sellerCarteGriseUploadComplete}
                              onDeleteComplete={sellerCarteGriseDeleteComplete}
                              uploadedUrls={sellerCarteGriseUrls}
                              isRequired={true}
                              onValidate={handleSellerCarteGriseValidation}
                            />
                          </Grid>

                          {/* Cerfa de déclaration de cession */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" component="div" textAlign={'center'}>
                              2 - Cerfa de déclaration de cession{' '}
                              <a
                                href="https://immatriculation.ants.gouv.fr/files/1e4341b6-4695-4f74-a51a-5ef962fba392/ef4ec059-07a7-4cc7-804f-e16944afeb5e/87e1c701-91dd-49de-86ed-52b7cb406dd0/cerfa_15776-01.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#fdd15f', textDecoration: 'underline' }}
                              >
                                (Cerfa 15776*01)
                              </a>
                            </Typography>
                            <FirebaseUploadComponent
                              onUploadComplete={sellerCerfaCessionUploadComplete}
                              onDeleteComplete={sellerCerfaCessionDeleteComplete}
                              uploadedUrls={sellerCerfaCessionUrls}
                              isRequired={true}
                              onValidate={handleSellerCerfaCessionValidation}
                            />
                          </Grid>

                          {/* Cerfa de demande d'immatriculation */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" component="div" textAlign={'center'}>
                              3 - Cerfa de demande d'immatriculation{' '}
                              <a
                                href="https://immatriculation.ants.gouv.fr/files/1e4341b6-4695-4f74-a51a-5ef962fba392/ef4ec059-07a7-4cc7-804f-e16944afeb5e/0b6477d5-86c9-43bb-bf65-08ade3d1ca37/cerfa_13750-05.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#fdd15f', textDecoration: 'underline' }}
                              >
                                (Cerfa 13750*05)
                              </a>
                            </Typography>
                            <FirebaseUploadComponent
                              onUploadComplete={sellerCerfaDemandeImmatUploadComplete}
                              onDeleteComplete={sellerCerfaDemandeImmatDeleteComplete}
                              uploadedUrls={sellerCerfaDemandeImmatUrls}
                              isRequired={true}
                              onValidate={handleSellerCerfaDemandeImmatValidation}
                            />
                          </Grid>

                          {/* Mandat à un professionnel */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" component="div" textAlign={'center'}>
                              4 - Mandat à un professionnel{' '}
                              <a
                                href="https://www.service-public.fr/simulateur/calcul/13757"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#fdd15f', textDecoration: 'underline' }}
                              >
                                (Cerfa 13757*03)
                              </a>
                            </Typography>
                            <FirebaseUploadComponent
                              onUploadComplete={sellerMandatUploadComplete}
                              onDeleteComplete={sellerMandatDeleteComplete}
                              uploadedUrls={sellerMandatUrls}
                              isRequired={true}
                              onValidate={handleSellerMandatValidation}
                            />
                          </Grid>

                          {/* Attestation d'assurance */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" component="div" textAlign={'center'}>
                              5 - Attestation d'assurance
                            </Typography>
                            <FirebaseUploadComponent
                              onUploadComplete={sellerAssuranceUploadComplete}
                              onDeleteComplete={sellerAssuranceDeleteComplete}
                              uploadedUrls={sellerAssuranceUrls}
                              isRequired={true}
                              onValidate={handleSellerAssuranceValidation}
                            />
                          </Grid>

                          {/* Contrôle technique */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" component="div" textAlign={'center'}>
                              6 - Contrôle technique
                            </Typography>
                            <FirebaseUploadComponent
                              onUploadComplete={sellerControleTechniqueUploadComplete}
                              onDeleteComplete={sellerControleTechniqueDeleteComplete}
                              uploadedUrls={sellerControleTechniqueUrls}
                              isRequired={true}
                              onValidate={handleSellerControleTechniqueValidation}
                            />
                          </Grid>
                        </Grid>
                      )}

                      {/* Messages d'erreur pour chaque dropzone du vendeur */}
                      {errors.sellerCarteGrise && (
                        <Typography color="error" align="center">
                          {errors.sellerCarteGrise.message}
                        </Typography>
                      )}
                      {errors.sellerCerfaCession && (
                        <Typography color="error" align="center">
                          {errors.sellerCerfaCession.message}
                        </Typography>
                      )}
                      {errors.sellerCerfaDemandeImmat && (
                        <Typography color="error" align="center">
                          {errors.sellerCerfaDemandeImmat.message}
                        </Typography>
                      )}
                      {errors.sellerMandat && (
                        <Typography color="error" align="center">
                          {errors.sellerMandat.message}
                        </Typography>
                      )}
                      {errors.sellerAssurance && (
                        <Typography color="error" align="center">
                          {errors.sellerAssurance.message}
                        </Typography>
                      )}
                      {errors.sellerControleTechnique && (
                        <Typography color="error" align="center">
                          {errors.sellerControleTechnique.message}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  <Divider
                    variant="fullWidth"
                    component="hr"
                    style={{
                      marginLeft: 24,
                      backgroundColor: '#fdd15f',
                      height: '2px',
                      width: 'calc(100% - 24px)',
                      border: 0,
                      marginTop: 30,
                      marginBottom: 30,
                    }}
                  />

                  <FormLabel component="legend">Ancien titulaire</FormLabel>
                  <Grid container alignItems="center" item xs={12} md={12}>
                    <Grid item>
                      <Typography variant="body2" component="div" sx={{ marginRight: 2 }}>
                        L'ancien propriétaire est un :
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Controller
                        name="sellerClientType"
                        control={control}
                        defaultValue="person"
                        render={({ field }) => (
                          <RadioGroup {...field} row>
                            <FormControlLabel
                              className="inputElement"
                              value="person"
                              disabled={isReadOnly}
                              control={<Radio />}
                              label="Particulier"
                            />
                            <FormControlLabel
                              disabled={isReadOnly}
                              className="inputElement"
                              value="pro"
                              control={<Radio />}
                              label="Pro"
                            />
                          </RadioGroup>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    {sellerClientType === 'person' && (
                      <>
                        <Grid item xs={12} md={3} alignItems="center" display={'flex'}>
                          <Controller
                            name="sellerGender"
                            control={control}
                            defaultValue="mr"
                            rules={sellerClientType === 'person' ? validationRules.sellerGender : {}}
                            render={({ field }) => (
                              <RadioGroup {...field} row>
                                <FormControlLabel
                                  value="mr"
                                  control={<Radio />}
                                  label="Monsieur"
                                  className="inputElement"
                                  disabled={isReadOnly}
                                />
                                <FormControlLabel
                                  disabled={isReadOnly}
                                  value="mme"
                                  control={<Radio />}
                                  label="Madame"
                                  className="inputElement"
                                />
                              </RadioGroup>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="sellerLastName"
                            control={control}
                            rules={sellerClientType === 'person' ? validationRules.sellerLastName : {}}
                            defaultValue={''}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled={isReadOnly}
                                id="sellerLastName"
                                error={!!errors.sellerLastName}
                                className="inputElement"
                                margin="dense"
                                label="Nom"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="sellerFirstName"
                            control={control}
                            rules={sellerClientType === 'person' ? validationRules.sellerFirstName : {}}
                            defaultValue={''}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled={isReadOnly}
                                error={!!errors.sellerFirstName}
                                className="inputElement"
                                margin="dense"
                                id="sellerFirstName"
                                label="Prénom"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="sellerUsualName"
                            rules={{}}
                            control={control}
                            defaultValue={''}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                id="sellerUsualName"
                                error={!!errors.sellerUsualName}
                                disabled={isReadOnly}
                                className="inputElement"
                                margin="dense"
                                label="Nom d'usage"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                    {sellerClientType === 'pro' && (
                      <>
                        <Grid item xs={12} md={4}>
                          <Controller
                            name="sellerSiret"
                            control={control}
                            rules={
                              sellerClientType === 'pro'
                                ? {
                                    required: 'Ce champ est requis',
                                    minLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                    maxLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                  }
                                : {
                                    minLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                    maxLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                  }
                            }
                            defaultValue={''}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled={isReadOnly}
                                className="inputElement"
                                error={!!errors.sellerSiret}
                                margin="dense"
                                id="sellerSiret"
                                label="SIRET"
                                type="text"
                                fullWidth
                                inputProps={{ maxLength: 14 }} // Limite le nombre de caractères que l'utilisateur peut taper
                                onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Controller
                            name="sellerCompanyName"
                            control={control}
                            rules={sellerClientType === 'pro' ? validationRules.sellerCompanyName : {}}
                            defaultValue={''}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled={isReadOnly}
                                margin="dense"
                                error={!!errors.sellerCompanyName}
                                id="sellerCompanyName"
                                className="inputElement"
                                label="Raison sociale"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Divider
                    variant="fullWidth"
                    component="hr"
                    style={{
                      marginLeft: 24,
                      backgroundColor: '#fdd15f',
                      height: '2px',
                      width: 'calc(100% - 24px)',
                      border: 0,
                      marginTop: 30,
                      marginBottom: 30,
                    }}
                  />
                  <FormLabel component="legend">Nouveau titulaire</FormLabel>
                  <Grid container alignItems="center" item xs={12} md={12}>
                    <Grid item>
                      <Typography variant="body2" component="div" sx={{ marginRight: 2 }}>
                        Le nouveau propriétaire est un :
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Controller
                        name="buyerClientType"
                        control={control}
                        defaultValue="person"
                        render={({ field }) => (
                          <RadioGroup {...field} row>
                            <FormControlLabel
                              disabled={isReadOnly}
                              className="inputElement"
                              value="person"
                              control={<Radio />}
                              label="Particulier"
                            />
                            <FormControlLabel
                              disabled={isReadOnly}
                              className="inputElement"
                              value="pro"
                              control={<Radio />}
                              label="Pro"
                            />
                          </RadioGroup>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                      <Grid container spacing={2}>
                        {buyerClientType === 'person' && (
                          <>
                            <Grid item xs={12} md={3} alignItems="center" display={'flex'}>
                              <Controller
                                name="buyerGender"
                                control={control}
                                rules={buyerClientType === 'person' ? validationRules.buyerGender : {}}
                                defaultValue="mr"
                                render={({ field }) => (
                                  <RadioGroup {...field} row>
                                    <FormControlLabel
                                      value="mr"
                                      control={<Radio />}
                                      label="Monsieur"
                                      className="inputElement"
                                      disabled={isReadOnly}
                                    />
                                    <FormControlLabel
                                      value="mme"
                                      control={<Radio />}
                                      label="Madame"
                                      className="inputElement"
                                      disabled={isReadOnly}
                                    />
                                  </RadioGroup>
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Controller
                                name="buyerLastName"
                                control={control}
                                rules={buyerClientType === 'person' ? validationRules.buyerLastName : {}}
                                defaultValue={''}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    id="buyerLastName"
                                    error={!!errors.buyerLastName}
                                    disabled={isReadOnly}
                                    className="inputElement"
                                    margin="dense"
                                    label="Nom"
                                    type="text"
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Controller
                                name="buyerFirstName"
                                control={control}
                                rules={buyerClientType === 'person' ? validationRules.buyerFirstName : {}}
                                defaultValue={''}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    error={!!errors.buyerFirstName}
                                    disabled={isReadOnly}
                                    className="inputElement"
                                    margin="dense"
                                    id="buyerFirstName"
                                    label="Prénom"
                                    type="text"
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12} md={3}>
                              <Controller
                                name="buyerUsualName"
                                control={control}
                                defaultValue={''}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    id="buyerUsualName"
                                    error={!!errors.buyerUsualName}
                                    disabled={isReadOnly}
                                    className="inputElement"
                                    margin="dense"
                                    label="Nom d'usage"
                                    type="text"
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>
                    {buyerClientType === 'pro' ? (
                      <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Controller
                              name="buyerSiret"
                              control={control}
                              rules={
                                buyerClientType === 'pro'
                                  ? {
                                      required: 'Ce champ est requis',
                                      minLength: {
                                        value: 14,
                                        message: 'Le SIRET doit contenir exactement 14 caractères',
                                      },
                                      maxLength: {
                                        value: 14,
                                        message: 'Le SIRET doit contenir exactement 14 caractères',
                                      },
                                    }
                                  : {
                                      minLength: {
                                        value: 14,
                                        message: 'Le SIRET doit contenir exactement 14 caractères',
                                      },
                                      maxLength: {
                                        value: 14,
                                        message: 'Le SIRET doit contenir exactement 14 caractères',
                                      },
                                    }
                              }
                              defaultValue={''}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  className="inputElement"
                                  error={!!errors.buyerSiret}
                                  disabled={isReadOnly}
                                  margin="dense"
                                  id="buyerSiret"
                                  label="SIRET"
                                  type="text"
                                  fullWidth
                                  inputProps={{ maxLength: 14 }} // Limite le nombre de caractères que l'utilisateur peut taper
                                  onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Controller
                              name="buyerCompanyName"
                              control={control}
                              rules={buyerClientType === 'pro' ? { required: 'Ce champ est requis' } : {}}
                              defaultValue={''}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  margin="dense"
                                  error={!!errors.buyerCompanyName}
                                  disabled={isReadOnly}
                                  id="buyerCompanyName"
                                  className="inputElement"
                                  label="Raison sociale"
                                  type="text"
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={6}></Grid>
                    )}
                  </Grid>
                  <Grid container item spacing={2}>
                    <Grid item xs={12} md={2}>
                      <Controller
                        name="buyerAddressNumber"
                        control={control}
                        rules={validationRules.addressNumber}
                        defaultValue={''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            disabled={isReadOnly}
                            error={!!errors.buyerAddressNumber}
                            className="inputElement"
                            margin="dense"
                            id="buyerAddressNumber"
                            label="Numéro d'adresse"
                            type="number"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Controller
                        name="buyerAddressExtension"
                        control={control}
                        defaultValue={''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            margin="dense"
                            disabled={isReadOnly}
                            id="buyerAddressExtension"
                            label="Extension d'adresse"
                            type="text"
                            fullWidth
                            inputProps={{ maxLength: 7 }}
                            placeholder="Ex: BIS"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Controller
                        name="buyerAddressType"
                        control={control}
                        defaultValue={''}
                        rules={{ required: 'Ce champ est requis' }}
                        render={({ field, fieldState: { error } }) => (
                          <FormControl fullWidth margin="dense" error={!!error}>
                            <InputLabel
                              margin="dense"
                              disabled={true}
                              className="inputElement"
                              id={`${field.name}-label`}
                            >
                              Type d'adresse
                            </InputLabel>
                            <Select
                              disabled={isReadOnly}
                              sx={{ minHeight: 'inherit', height: '56px' }}
                              className="inputElement"
                              {...field}
                              labelId={`${field.name}-label`}
                              id={field.name}
                              label="Type d'adresse"
                              value={field.value ? field.value.toLowerCase() : ''}
                            >
                              <MenuItem value="rue">Rue</MenuItem>
                              <MenuItem value="all">Allée</MenuItem>
                              <MenuItem value="avn">Avenue</MenuItem>
                              <MenuItem value="blvd">Boulevard</MenuItem>
                              <MenuItem value="chau">Chaussée</MenuItem>
                              <MenuItem value="che">Chemin</MenuItem>
                              <MenuItem value="cour">Cour</MenuItem>
                              <MenuItem value="fbg">Faubourg</MenuItem>
                              <MenuItem value="fort">Fort</MenuItem>
                              <MenuItem value="imp">Impasse</MenuItem>
                              <MenuItem value="ldit">Lieu-Dit</MenuItem>
                              <MenuItem value="parc">Parc</MenuItem>
                              <MenuItem value="pass">Passage</MenuItem>
                              <MenuItem value="plc">Place</MenuItem>
                              <MenuItem value="quai">Quai</MenuItem>
                              <MenuItem value="rte">Route</MenuItem>
                              <MenuItem value="sent">Sentier</MenuItem>
                              <MenuItem value="squa">Square</MenuItem>
                              <MenuItem value="sans">Inconnu</MenuItem>
                            </Select>
                            {error && <FormHelperText>{error.message}</FormHelperText>}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="buyerAddressName"
                        control={control}
                        rules={validationRules.addressName}
                        defaultValue={cgData?.buyerAddressName ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            disabled={isReadOnly}
                            className="inputElement"
                            error={!!errors.buyerAddressName}
                            margin="dense"
                            id="buyerAddressName"
                            label="Nom de la voie (Rue, Allée, ou Avenue, ne doivent pas apparaitre ici)"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="buyerAddressPlace"
                        control={control}
                        rules={{}}
                        defaultValue={cgData?.buyerAddressPlace ?? ''}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            disabled={isReadOnly}
                            className="inputElement"
                            error={!!error}
                            helperText={error ? error.message : ''}
                            margin="dense"
                            id="buyerAddressPlace"
                            label="Lieu-dit"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="buyerAddressStairs"
                        control={control}
                        rules={{}}
                        defaultValue={cgData?.buyerAddressStairs ?? ''}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            disabled={isReadOnly}
                            className="inputElement"
                            error={!!error}
                            helperText={error ? error.message : ''}
                            margin="dense"
                            id="buyerAddressStairs"
                            label="Étage/Escalier/Appartement"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="buyerAddressComp"
                        control={control}
                        rules={{}}
                        defaultValue={cgData?.buyerAddressComp ?? ''}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            disabled={isReadOnly}
                            className="inputElement"
                            error={!!error}
                            helperText={error ? error.message : ''}
                            margin="dense"
                            id="buyerAddressComp"
                            label="Complément d'adresse"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="buyerAddressPostalBox"
                        control={control}
                        rules={{}}
                        defaultValue={cgData?.buyerAddressPostalBox ?? ''}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            disabled={isReadOnly}
                            className="inputElement"
                            error={!!error}
                            helperText={error ? error.message : ''}
                            margin="dense"
                            id="buyerAddressPostalBox"
                            label="Boîte postale"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {registrationIdGetParameter && isLoadingCGData ? (
                        <CircularProgress sx={{ marginRight: 2 }} size={24} />
                      ) : registrationIdGetParameter ? (
                        <TextField
                          value={buyerPostalCode}
                          disabled={true}
                          className="inputElement"
                          error={!!errors.buyerPostalCode}
                          margin="dense"
                          id="buyerPostalCode"
                          label="Code postal"
                          InputLabelProps={{ shrink: true }}
                          type="text"
                          fullWidth
                        />
                      ) : (
                        <Controller
                          name="buyerPostalCode"
                          control={control}
                          rules={validationRules.postalCode}
                          defaultValue={cgData?.BuyerPostalCode ?? ''}
                          render={({ field }) => (
                            <Autocomplete
                              freeSolo
                              disablePortal
                              disabled={isReadOnly}
                              className="inputElement"
                              options={cityOptions}
                              getOptionLabel={(option: any) => `${option.label} (${option.value})`}
                              isOptionEqualToValue={(option, value) => option.value === value}
                              onInputChange={(event, value) => handlePostalCodeChange(event, value)}
                              onChange={(event, value) => {
                                const postalCode = value ? value.value : '';
                                const city = value ? value.label : '';
                                setSelectedPostalCode(postalCode);
                                setSelectedCity(city);
                                field.onChange(postalCode);
                                setValue('buyerCity', city);
                                setValue('buyerCountry', 'France'); // Met à jour automatiquement le champ "Pays"
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  sx={{
                                    minHeight: 'inherit',
                                    height: '56px',
                                    '& .MuiOutlinedInput-root': { height: '56px' },
                                  }}
                                  value={selectedPostalCode ?? cgData?.buyerPostalCode}
                                  onChange={(event) => {
                                    setSelectedPostalCode(event.target.value);
                                    field.onChange(event.target.value);
                                  }}
                                  error={!!errors.buyerPostalCode}
                                  margin="dense"
                                  id="buyerPostalCode"
                                  label="Code postal"
                                  type="text"
                                  fullWidth
                                />
                              )}
                            />
                          )}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Controller
                        name="buyerCity"
                        control={control}
                        rules={validationRules.city}
                        defaultValue={(cgData && cgData?.buyerCity) ?? ''}
                        render={({ field }) => (
                          <Autocomplete
                            disablePortal
                            freeSolo
                            disabled={isReadOnly}
                            className="inputElement"
                            id="buyerCity"
                            options={cityOptions}
                            getOptionLabel={(option: any) => (typeof option === 'string' ? option : option.label)}
                            isOptionEqualToValue={(option, value) =>
                              typeof option === 'string' ? option === value : option.label === value
                            }
                            value={selectedCity || field.value || ''}
                            onChange={(event, value) => {
                              if (value) {
                                const cityName = typeof value === 'string' ? value : value.label;
                                const cityCode = typeof value === 'string' ? '' : value.value;
                                setSelectedCity(cityName);
                                field.onChange(cityName);

                                // Mettre à jour le code postal si on a sélectionné une ville
                                if (cityCode) {
                                  setSelectedPostalCode(cityCode);
                                  setValue('buyerPostalCode', cityCode);
                                }

                                // Mettre à jour le pays
                                setValue('buyerCountry', 'France');
                              }
                            }}
                            onInputChange={(event, newInputValue) => {
                              setSelectedCity(newInputValue);
                              field.onChange(newInputValue);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  minHeight: 'inherit',
                                  height: '56px',
                                  '& .MuiOutlinedInput-root': { height: '56px' },
                                }}
                                disabled={isReadOnly}
                                error={!!errors.buyerCity}
                                margin="dense"
                                label="Ville"
                                fullWidth
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="buyerCountry"
                        control={control}
                        rules={validationRules.country}
                        defaultValue={(cgData && cgData?.buyerCountry) ?? 'France'}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            disabled={isReadOnly}
                            className="inputElement"
                            error={!!errors.buyerCountry}
                            margin="dense"
                            id="buyerCountry"
                            label="Pays"
                            type="text"
                            fullWidth
                            value={field.value || 'France'}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="buyerPhone"
                        control={control}
                        rules={{
                          required: 'Ce champ est requis',
                          validate: (value) =>
                            isValidPhoneNumber(value) ||
                            'Le numéro doit être un portable français valide (06 ou 07) et de la forme +33 7 12 34 56 78',
                        }}
                        defaultValue={toFrPhoneInputValue(cgData?.buyerPhone ?? cgData?.BuyerPhone)}
                        render={({ field }) => (
                          <PhoneInput
                            {...field}
                            country={'fr'}
                            disabled={isReadOnly}
                            inputStyle={{
                              fontFamily: 'Urbanist',
                              fontWeight: 500,
                              color: 'black',
                              width: '100%',
                              marginTop: '8px',
                              height: '56px',
                            }}
                            specialLabel="Numéro de téléphone portable"
                            onChange={(phone) => {
                              // Valeur widget (33…) pour l'affichage ; toStorage à la soumission.
                              field.onChange(phone);
                            }}
                            onlyCountries={['fr']} // Limite la sélection à la France uniquement
                          />
                        )}
                      />
                      {errors.buyerPhone && (
                        <Typography color="error" variant="body2">
                          {errors.buyerPhone.message}
                        </Typography>
                      )}
                    </Grid>
                    {buyerClientType === 'person' && (
                      <>
                        <Grid container alignItems="center" item xs={12} md={12}>
                          <Grid item xs={6} md={3}>
                            <Typography variant="body2" component="div" sx={{ marginRight: 2 }}>
                              Type d'hébergement :
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Controller
                              name="buyerResidenceType"
                              control={control}
                              defaultValue="owner"
                              render={({ field }) => (
                                <RadioGroup {...field} row>
                                  <FormControlLabel
                                    className="inputElement"
                                    value="owner"
                                    disabled={isReadOnly}
                                    control={<Radio />}
                                    label="Titulaire"
                                  />
                                  <FormControlLabel
                                    className="inputElement"
                                    value="guest"
                                    disabled={isReadOnly}
                                    control={<Radio />}
                                    label="Hébergé"
                                  />
                                </RadioGroup>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            {buyerResidenceType === 'owner' ? (
                              // Affichage pour titulaire (une seule dropzone)
                              <>
                                <Typography variant="body2" component="div" textAlign={'center'}>
                                  Justificatif de domicile
                                </Typography>
                                {cgData ? (
                                  cgData.buyerResidencyFilesUrls && (
                                    <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgData.buyerResidencyFilesUrls} />
                                  )
                                ) : (
                                  <FirebaseUploadComponent
                                    onUploadComplete={(url) => buyerResidencyFilesUploadComplete(url)}
                                    onDeleteComplete={(url) => buyerResidencyFilesDeleteComplete(url)}
                                    uploadedUrls={buyerResidencyFilesUrls}
                                    isRequired={buyerClientType === 'person'}
                                    onValidate={handleBuyerResidencyValidation}
                                  />
                                )}
                                {errors.buyerResidency && (
                                  <Typography color="error" align="center">
                                    {errors.buyerResidency.message}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              // Affichage pour hébergé (4 dropzones séparées)
                              <>
                                <Typography variant="body2" component="div" textAlign={'center'}>
                                  Documents d'hébergement
                                </Typography>

                                {/* Vérifier si on a des documents structurés ou legacy pour la résidence */}
                                {cgData ? (
                                  // Mode lecture seule - vérifier le type de données
                                  (() => {
                                    const hasStructuredResidencyDocuments =
                                      cgDocumentsStructured?.data?.buyerResidencyFiles &&
                                      (cgDocumentsStructured.data.buyerResidencyFiles.hostProofOfResidence?.length >
                                        0 ||
                                        cgDocumentsStructured.data.buyerResidencyFiles.hostIdentity?.length > 0 ||
                                        cgDocumentsStructured.data.buyerResidencyFiles.honorAttestation?.length > 0 ||
                                        cgDocumentsStructured.data.buyerResidencyFiles.guestProofOfResidence?.length >
                                          0);

                                    const hasLegacyResidencyDocuments =
                                      cgDocumentsStructured?.data?.buyerResidencyFiles?.legacy?.length > 0 ||
                                      cgData.buyerResidencyFilesUrls;

                                    // Si on a des documents structurés ET pas de documents legacy, afficher les dropzones séparées
                                    if (hasStructuredResidencyDocuments && !hasLegacyResidencyDocuments) {
                                      // Afficher les 4 dropzones séparées avec documents structurés
                                      return (
                                        <Grid container spacing={1} sx={{ mt: 1 }}>
                                          {/* Justificatif de domicile de l'hébergeur */}
                                          <Grid item xs={12} md={6}>
                                            <Typography variant="caption" component="div" textAlign={'center'}>
                                              1 - Justificatif de domicile de l'hébergeur
                                            </Typography>
                                            {cgDocumentsStructured?.data?.buyerResidencyFiles?.hostProofOfResidence
                                              ?.length > 0 && (
                                              <FilesOpener
                                                onFileClick={handleCarouselOpen}
                                                FilesUrls={cgDocumentsStructured.data.buyerResidencyFiles.hostProofOfResidence.join(
                                                  ',',
                                                )}
                                              />
                                            )}
                                          </Grid>

                                          {/* Pièce d'identité de l'hébergeur */}
                                          <Grid item xs={12} md={6}>
                                            <Typography variant="caption" component="div" textAlign={'center'}>
                                              2 - Pièce d'identité de l'hébergeur
                                            </Typography>
                                            {cgDocumentsStructured?.data?.buyerResidencyFiles?.hostIdentity?.length >
                                              0 && (
                                              <FilesOpener
                                                onFileClick={handleCarouselOpen}
                                                FilesUrls={cgDocumentsStructured.data.buyerResidencyFiles.hostIdentity.join(
                                                  ',',
                                                )}
                                              />
                                            )}
                                          </Grid>

                                          {/* Attestation sur l'honneur */}
                                          <Grid item xs={12} md={6}>
                                            <Typography variant="caption" component="div" textAlign={'center'}>
                                              3 - Attestation sur l'honneur
                                            </Typography>
                                            {cgDocumentsStructured?.data?.buyerResidencyFiles?.honorAttestation
                                              ?.length > 0 && (
                                              <FilesOpener
                                                onFileClick={handleCarouselOpen}
                                                FilesUrls={cgDocumentsStructured.data.buyerResidencyFiles.honorAttestation.join(
                                                  ',',
                                                )}
                                              />
                                            )}
                                          </Grid>

                                          {/* Justificatif de domicile */}
                                          <Grid item xs={12} md={6}>
                                            <Typography variant="caption" component="div" textAlign={'center'}>
                                              4 - Justificatif de domicile
                                            </Typography>
                                            {cgDocumentsStructured?.data?.buyerResidencyFiles?.guestProofOfResidence
                                              ?.length > 0 && (
                                              <FilesOpener
                                                onFileClick={handleCarouselOpen}
                                                FilesUrls={cgDocumentsStructured.data.buyerResidencyFiles.guestProofOfResidence.join(
                                                  ',',
                                                )}
                                              />
                                            )}
                                          </Grid>
                                        </Grid>
                                      );
                                    } else {
                                      // Afficher une seule section avec tous les fichiers legacy
                                      return (
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                          <Grid item xs={12}>
                                            {cgDocumentsStructured?.data?.buyerResidencyFiles?.legacy?.length > 0 ? (
                                              <FilesOpener
                                                onFileClick={handleCarouselOpen}
                                                FilesUrls={cgDocumentsStructured.data.buyerResidencyFiles.legacy.join(
                                                  ',',
                                                )}
                                              />
                                            ) : (
                                              cgData.buyerResidencyFilesUrls && (
                                                <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgData.buyerResidencyFilesUrls} />
                                              )
                                            )}
                                          </Grid>
                                        </Grid>
                                      );
                                    }
                                  })()
                                ) : (
                                  // Mode création - afficher les 4 dropzones
                                  <Grid container spacing={1} sx={{ mt: 1 }}>
                                    {/* Justificatif de domicile de l'hébergeur */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{ fontSize: '16px' }}
                                        component="div"
                                        textAlign={'center'}
                                      >
                                        1 - Justificatif de domicile de l'hébergeur
                                      </Typography>
                                      <FirebaseUploadComponent
                                        onUploadComplete={buyerResidencyHostProofUploadComplete}
                                        onDeleteComplete={buyerResidencyHostProofDeleteComplete}
                                        uploadedUrls={buyerResidencyHostProofUrls}
                                        isRequired={true}
                                        onValidate={handleBuyerResidencyHostProofValidation}
                                      />
                                    </Grid>

                                    {/* Pièce d'identité de l'hébergeur */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{ fontSize: '16px' }}
                                        component="div"
                                        textAlign={'center'}
                                      >
                                        2 - Pièce d'identité de l'hébergeur
                                      </Typography>
                                      <FirebaseUploadComponent
                                        onUploadComplete={buyerResidencyHostIdentityUploadComplete}
                                        onDeleteComplete={buyerResidencyHostIdentityDeleteComplete}
                                        uploadedUrls={buyerResidencyHostIdentityUrls}
                                        isRequired={true}
                                        onValidate={handleBuyerResidencyHostIdentityValidation}
                                      />
                                    </Grid>

                                    {/* Attestation sur l'honneur */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{ fontSize: '16px' }}
                                        component="div"
                                        textAlign={'center'}
                                      >
                                        3 - Attestation sur l'honneur
                                      </Typography>
                                      <FirebaseUploadComponent
                                        onUploadComplete={buyerResidencyHonorAttestationUploadComplete}
                                        onDeleteComplete={buyerResidencyHonorAttestationDeleteComplete}
                                        uploadedUrls={buyerResidencyHonorAttestationUrls}
                                        isRequired={true}
                                        onValidate={handleBuyerResidencyHonorAttestationValidation}
                                      />
                                    </Grid>

                                    {/* Justificatif de domicile */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="caption"
                                        sx={{ fontSize: '16px' }}
                                        component="div"
                                        textAlign={'center'}
                                      >
                                        4 - Justificatif de domicile
                                      </Typography>
                                      <FirebaseUploadComponent
                                        onUploadComplete={buyerResidencyGuestProofUploadComplete}
                                        onDeleteComplete={buyerResidencyGuestProofDeleteComplete}
                                        uploadedUrls={buyerResidencyGuestProofUrls}
                                        isRequired={true}
                                        onValidate={handleBuyerResidencyGuestProofValidation}
                                      />
                                    </Grid>
                                  </Grid>
                                )}

                                {/* Messages d'erreur pour chaque dropzone de résidence */}
                                {errors.buyerResidencyHostProof && (
                                  <Typography color="error" align="center">
                                    {errors.buyerResidencyHostProof.message}
                                  </Typography>
                                )}
                                {errors.buyerResidencyHostIdentity && (
                                  <Typography color="error" align="center">
                                    {errors.buyerResidencyHostIdentity.message}
                                  </Typography>
                                )}
                                {errors.buyerResidencyHonorAttestation && (
                                  <Typography color="error" align="center">
                                    {errors.buyerResidencyHonorAttestation.message}
                                  </Typography>
                                )}
                                {errors.buyerResidencyGuestProof && (
                                  <Typography color="error" align="center">
                                    {errors.buyerResidencyGuestProof.message}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Grid>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Controller
                            name="buyerDateOfBirth"
                            rules={validationRules.buyerDateOfBirth}
                            control={control}
                            defaultValue=""
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                disabled={isReadOnly}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                margin="dense"
                                className="inputElement"
                                id="buyerDateOfBirth"
                                label="Date de naissance"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                // Ajouter des contraintes min/max
                                inputProps={{
                                  min: new Date(new Date().getFullYear() - 120, 0, 1).toISOString().slice(0, 10),
                                  max: new Date(new Date().getFullYear() - 17, 11, 31).toISOString().slice(0, 10),
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          {registrationIdGetParameter ? (
                            <TextField
                              sx={{
                                minHeight: 'inherit',
                                height: '56px',
                                '& .MuiOutlinedInput-root': { height: '56px' },
                              }}
                              className="inputElement"
                              value={cgData.BuyerPlaceOfBirth}
                              disabled={isReadOnly}
                              error={!!errors.buyerPlaceOfBirth}
                              margin="dense"
                              label="Lieu de naissance"
                              fullWidth
                            />
                          ) : (
                            <Controller
                              name="buyerPlaceOfBirth"
                              control={control}
                              rules={buyerClientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                              render={({ field }) => (
                                <Autocomplete
                                  disablePortal
                                  freeSolo
                                  disabled={isReadOnly}
                                  className="inputElement"
                                  id="buyerPlaceOfBirth"
                                  options={birthDepartmentOptions}
                                  getOptionLabel={(option: any) => `${option.label} (${option.value})`}
                                  isOptionEqualToValue={(option, value) => option.label === value.label}
                                  onInputChange={handleBirthCityChange}
                                  onChange={(event, value) => {
                                    field.onChange(value ? value.label : '');
                                    const department = value ? value.value.slice(0, 2) : '';
                                    setValue('buyerDepartmentOfBirth', department, {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    });
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      sx={{
                                        minHeight: 'inherit',
                                        height: '56px',
                                        '& .MuiOutlinedInput-root': { height: '56px' },
                                      }}
                                      {...params}
                                      {...field}
                                      value={(cgData && cgData?.buyerPlaceOfBirth) ?? ''}
                                      disabled={isReadOnly}
                                      error={!!errors.buyerPlaceOfBirth}
                                      margin="dense"
                                      label="Lieu de naissance"
                                      fullWidth
                                    />
                                  )}
                                />
                              )}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Controller
                            name="buyerDepartmentOfBirth"
                            control={control}
                            rules={{
                              validate: {
                                required: (value) =>
                                  isFrance ? (value.trim() === '' ? 'Ce champ est requis pour la France' : true) : true,
                                departmentFormat: (value) => {
                                  if (!isFrance || !value.trim()) return true;
                                  // Regex pour accepter les départements français : 2 à 3 chiffres ou 2A/2B pour la Corse
                                  const deptRegex = /^(?:[0-9]{2,3}|2[AB])$/;
                                  return (
                                    deptRegex.test(value.trim()) ||
                                    'Format invalide. Entrez un numéro de département de 2 ou 3 chiffres (ex: 01, 77, 971, 2A, 2B)'
                                  );
                                },
                              },
                            }}
                            render={({ field, fieldState: { error } }) => {
                              const isDisabled = !isFrance || isReadOnly;
                              const hasValue = !!field.value && field.value.trim() !== '';
                              const showClearButton = isDisabled && hasValue;

                              return (
                                <Box sx={{ position: 'relative' }}>
                                  <TextField
                                    {...field}
                                    onChange={(e) => {
                                      // Convertir en majuscules pour 2a/2b et limiter les caractères autorisés
                                      const value = e.target.value.toUpperCase();
                                      // Autoriser seulement les chiffres et les lettres A/B après un 2
                                      const filteredValue = value.replace(/[^0-9AB]/g, '').slice(0, 3);
                                      field.onChange(filteredValue);
                                    }}
                                    label="Numéro de département de naissance"
                                    variant="outlined"
                                    margin="dense"
                                    className="inputElement"
                                    fullWidth
                                    disabled={isDisabled}
                                    error={!!error}
                                    helperText={error ? error.message : ''}
                                    inputProps={{ maxLength: 3 }}
                                    InputLabelProps={{
                                      style: {
                                        fontWeight: 'bold', // Mettre le label en gras
                                        fontSize: '1rem', // Réduire la taille de la police si nécessaire
                                      },
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        paddingRight: showClearButton ? '40px' : '14px',
                                      },
                                    }}
                                  />
                                  {showClearButton && (
                                    <IconButton
                                      onClick={() => {
                                        field.onChange('');
                                        setValue('buyerDepartmentOfBirth', '', {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        });
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        right: '8px',
                                        top: '55%',
                                        transform: 'translateY(-50%)',
                                        color: '#d32f2f',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': {
                                          backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                          color: '#b71c1c',
                                        },
                                        zIndex: 1,
                                        padding: '4px',
                                      }}
                                      size="small"
                                      aria-label="Réinitialiser le département"
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              );
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="buyerCountryOfBirth"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled={isReadOnly}
                                className="inputElement"
                                label="Pays de naissance"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                error={!!errors.buyerCountryOfBirth}
                                helperText={errors.buyerCountryOfBirth ? errors.buyerCountryOfBirth.message : ''}
                              />
                            )}
                          />
                        </Grid>
                        {/*
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="buyerDocumentId"
                            rules={buyerClientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                            control={control}
                            defaultValue={''}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                margin="dense"
                                error={!!errors.buyerDocumentId}
                                className="inputElement"
                                id="buyerDocumentId"
                                label="ID du document"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Controller
                            name="buyerDocumentType"
                            rules={buyerClientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                            control={control}
                            defaultValue={''}
                            render={({ field }) => (
                              <Select
                                {...field}
                                disabled={isReadOnly}
                                error={!!errors.buyerDocumentType}
                                fullWidth
                                margin="dense"
                                className="inputElement"
                                displayEmpty
                              >
                                <MenuItem value="" disabled>
                                  Type de document
                                </MenuItem>
                                <MenuItem value="id_card">Carte d'identité</MenuItem>
                                <MenuItem value="passport">Passeport</MenuItem>
                                <MenuItem value="driving_licence">Permis de conduire</MenuItem>
                                <MenuItem value="residence_permit">Titre de séjour</MenuItem>
                              </Select>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="buyerDocumentIssueDate"
                            rules={buyerClientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                            control={control}
                            defaultValue={''}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                sx={{ marginTop: '0px' }}
                                margin="dense"
                                error={!!errors.buyerDocumentIssueDate}
                                className="inputElement"
                                id="buyerDocumentIssueDate"
                                label="Date de délivrance du document"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                          />
                        </Grid>
                        */}
                      </>
                    )}
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" component="div" textAlign={'center'}>
                        {buyerClientType === 'person'
                          ? "Pièce d'identité & Permis de conduire"
                          : 'Pièce d’identité du gérant et Kbis'}
                      </Typography>

                      {/* Vérifier si on a des documents structurés ou legacy pour l'acheteur */}
                      {cgData ? (
                        // Mode lecture seule - vérifier le type de données
                        (() => {
                          const hasStructuredBuyerDocuments =
                            cgDocumentsStructured?.data?.buyerFiles &&
                            (cgDocumentsStructured.data.buyerFiles.identity?.length > 0 ||
                              cgDocumentsStructured.data.buyerFiles.license?.length > 0);

                          const hasLegacyBuyerDocuments =
                            cgDocumentsStructured?.data?.buyerFiles?.legacy?.length > 0 || cgData.buyerFilesUrls;

                          // Si on a des documents structurés ET pas de documents legacy, afficher les dropzones séparées
                          if (hasStructuredBuyerDocuments && !hasLegacyBuyerDocuments) {
                            // Afficher les 2 dropzones séparées avec documents structurés
                            return (
                              <Grid container spacing={2} sx={{ mt: 2 }}>
                                {/* Pièce d'identité */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" component="div" textAlign={'center'}>
                                    {buyerClientType === 'person' ? "Pièce d'identité" : "Pièce d'identité du gérant"}
                                  </Typography>
                                  {cgDocumentsStructured?.data?.buyerFiles?.identity?.length > 0 && (
                                    <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgDocumentsStructured.data.buyerFiles.identity.join(',')} />
                                  )}
                                </Grid>

                                {/* Permis de conduire */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" component="div" textAlign={'center'}>
                                    {buyerClientType === 'person' ? 'Permis de conduire' : 'Kbis'}
                                  </Typography>
                                  {cgDocumentsStructured?.data?.buyerFiles?.license?.length > 0 && (
                                    <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgDocumentsStructured.data.buyerFiles.license.join(',')} />
                                  )}
                                </Grid>
                              </Grid>
                            );
                          } else {
                            // Afficher une seule section avec tous les fichiers legacy
                            return (
                              <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={12}>
                                  {cgDocumentsStructured?.data?.buyerFiles?.legacy?.length > 0 ? (
                                    <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgDocumentsStructured.data.buyerFiles.legacy.join(',')} />
                                  ) : (
                                    cgData.buyerFilesUrls && <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgData.buyerFilesUrls} />
                                  )}
                                </Grid>
                              </Grid>
                            );
                          }
                        })()
                      ) : (
                        // Mode création - afficher les 2 dropzones
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                          {/* Pièce d'identité */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" component="div" textAlign={'center'}>
                              {buyerClientType === 'person' ? "Pièce d'identité" : "Pièce d'identité du gérant"}
                            </Typography>
                            <FirebaseUploadComponent
                              onUploadComplete={buyerIdentityUploadComplete}
                              onDeleteComplete={buyerIdentityDeleteComplete}
                              uploadedUrls={buyerIdentityUrls}
                              isRequired={true}
                              onValidate={handleBuyerIdentityValidation}
                            />
                          </Grid>

                          {/* Permis de conduire */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" component="div" textAlign={'center'}>
                              {buyerClientType === 'person' ? 'Permis de conduire' : 'Kbis'}
                            </Typography>
                            <FirebaseUploadComponent
                              onUploadComplete={buyerLicenseUploadComplete}
                              onDeleteComplete={buyerLicenseDeleteComplete}
                              uploadedUrls={buyerLicenseUrls}
                              isRequired={true}
                              onValidate={handleBuyerLicenseValidation}
                            />
                          </Grid>
                        </Grid>
                      )}

                      {/* Messages d'erreur pour chaque dropzone de l'acheteur */}
                      {errors.buyerIdentity && (
                        <Typography color="error" align="center">
                          {errors.buyerIdentity.message}
                        </Typography>
                      )}
                      {errors.buyerLicense && (
                        <Typography color="error" align="center">
                          {errors.buyerLicense.message}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                  {cobuyer === 'yes' && (
                    <Divider
                      variant="fullWidth"
                      component="hr"
                      style={{
                        marginLeft: 24,
                        backgroundColor: '#fdd15f',
                        height: '2px',
                        width: 'calc(100% - 24px)',
                        border: 0,
                        marginTop: 15,
                      }}
                    />
                  )}
                  <Grid container sx={{ marginTop: 2 }} alignItems="center" item xs={12} md={12}>
                    <Grid item>
                      <Typography variant="body1" component="div" sx={{ marginRight: 2 }}>
                        Il y a un co-titulaire :
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Controller
                        name="cobuyer"
                        control={control}
                        defaultValue="no"
                        render={({ field }) => (
                          <RadioGroup {...field} row>
                            <FormControlLabel
                              disabled={isReadOnly}
                              className="inputElement"
                              value="no"
                              control={<Radio />}
                              label="Non"
                            />
                            <FormControlLabel
                              disabled={isReadOnly}
                              className="inputElement"
                              value="yes"
                              control={<Radio />}
                              label="Oui"
                            />
                          </RadioGroup>
                        )}
                      />
                    </Grid>
                  </Grid>
                  {cobuyer === 'yes' && (
                    <>
                      <Grid container sx={{ marginTop: 2 }} alignItems="center" item xs={12} md={12}>
                        <Grid item>
                          <Typography variant="h6" sx={{ marginRight: 2 }}>
                            Le co-titulaire est un :
                          </Typography>
                        </Grid>

                        <Grid item xs={6} sm={4}>
                          <Controller
                            name="coBuyerClientType"
                            control={control}
                            defaultValue="person"
                            render={({ field }) => (
                              <RadioGroup {...field} row>
                                <FormControlLabel
                                  disabled={isReadOnly}
                                  className="inputElement"
                                  value="person"
                                  control={<Radio />}
                                  label="Particulier"
                                />
                                <FormControlLabel
                                  className="inputElement"
                                  value="pro"
                                  control={<Radio />}
                                  label="Professionnel"
                                  disabled={isReadOnly}
                                />
                              </RadioGroup>
                            )}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2} alignItems="center">
                        {coBuyerClientType === 'person' ? (
                          <>
                            <Grid item xs={6} md={3}>
                              <Controller
                                name="coBuyerLastName"
                                control={control}
                                rules={coBuyerClientType === 'person' ? validationRules.coBuyerLastName : {}}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    disabled={isReadOnly}
                                    label="Nom"
                                    fullWidth
                                    margin="dense"
                                    className="inputElement"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Controller
                                name="coBuyerFirstName"
                                control={control}
                                rules={coBuyerClientType === 'person' ? validationRules.coBuyerFirstName : {}}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label="Prénom"
                                    disabled={isReadOnly}
                                    fullWidth
                                    margin="dense"
                                    className="inputElement"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Controller
                                name="coBuyerUsualName"
                                control={control}
                                rules={{}}
                                defaultValue={''}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    disabled={isReadOnly}
                                    id="coBuyerUsualName"
                                    error={!!errors.coBuyerUsualName}
                                    className="inputElement"
                                    margin="dense"
                                    label="Nom d'usage"
                                    type="text"
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid item xs={6} md={4} key="coBuyerCompanyName">
                              <Controller
                                name="coBuyerCompanyName"
                                control={control}
                                rules={coBuyerClientType === 'pro' ? { required: 'Ce champ est requis' } : {}}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    disabled={isReadOnly}
                                    label="Raison sociale"
                                    fullWidth
                                    margin="dense"
                                    className="inputElement"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={6} md={4} key="coBuyerSiret">
                              <Controller
                                name="coBuyerSiret"
                                control={control}
                                rules={
                                  coBuyerClientType === 'pro'
                                    ? {
                                        required: 'Ce champ est requis',
                                        minLength: {
                                          value: 14,
                                          message: 'Le SIRET doit contenir exactement 14 caractères',
                                        },
                                        maxLength: {
                                          value: 14,
                                          message: 'Le SIRET doit contenir exactement 14 caractères',
                                        },
                                      }
                                    : {
                                        minLength: {
                                          value: 14,
                                          message: 'Le SIRET doit contenir exactement 14 caractères',
                                        },
                                        maxLength: {
                                          value: 14,
                                          message: 'Le SIRET doit contenir exactement 14 caractères',
                                        },
                                      }
                                }
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    disabled={isReadOnly}
                                    label="SIRET"
                                    fullWidth
                                    margin="dense"
                                    className="inputElement"
                                    error={!!errors.coBuyerSiret}
                                    helperText={errors.coBuyerSiret ? (errors.coBuyerSiret.message as string) : ''}
                                    inputProps={{ maxLength: 14 }}
                                    onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                                  />
                                )}
                              />
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12} md={coBuyerClientType === 'person' ? 3 : 4}>
                          <Typography variant="body2" component="div" textAlign={'center'}>
                            {coBuyerClientType === 'person'
                              ? "Pièce d'identité*"
                              : "KBIS et Pièce d'identité du gérant*"}
                          </Typography>
                          {cgData ? (
                            cgData.coBuyerFilesUrls && <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgData.coBuyerFilesUrls} />
                          ) : (
                            <FirebaseUploadComponent
                              onUploadComplete={(url) => coBuyerFilesUploadComplete(url)}
                              onDeleteComplete={(url) => coBuyerFilesDeleteComplete(url)}
                              uploadedUrls={coBuyerFilesUrls}
                              isRequired={cobuyer === 'yes'}
                              onValidate={handleCoBuyerFilesValidation}
                            />
                          )}
                          {errors.coBuyerFiles && (
                            <Typography color="error" align="center">
                              {errors.coBuyerFiles.message}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {/* Section pour afficher les fichiers des commentaires externes */}
                  {cgData && cgData.commentFilesUrls && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          📎 Fichiers des commentaires externes
                        </Typography>
                        <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgData.commentFilesUrls} />
                      </Grid>
                    </Grid>
                  )}

                  {/* Section pour afficher les fichiers des commentaires internes */}
                  {cgData && cgData.internalCommentFilesUrls && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          📎 Fichiers des commentaires internes
                        </Typography>
                        <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cgData.internalCommentFilesUrls} />
                      </Grid>
                    </Grid>
                  )}

                  <Grid container spacing={2} mt={2} justifyContent={'right'}>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}>
                      {!isReadOnly && (
                        <Button
                          disabled={
                            !isValid ||
                            isLoading ||
                            !sellerCarteGriseValid ||
                            !sellerCerfaCessionValid ||
                            !sellerCerfaDemandeImmatValid ||
                            !sellerMandatValid ||
                            !sellerAssuranceValid ||
                            !sellerControleTechniqueValid ||
                            !buyerIdentityValid ||
                            !buyerLicenseValid ||
                            (buyerClientType === 'person' && !buyerResidencyValidForStructured) ||
                            (cobuyer === 'yes' && !coBuyerFilesValid)
                          }
                          onClick={handleConfirmClick}
                          variant="contained"
                          className="dealActionButton"
                          sx={{
                            marginTop: 1,
                          }}
                        >
                          {isLoading && <CircularProgress sx={{ marginRight: 2 }} size={24} />} Soumettre la demande
                        </Button>
                      )}
                      {(!isValid ||
                        !watch('sellerGender') ||
                        !watch('buyerGender') ||
                        (cobuyer === 'yes' && !watch('coBuyerGender')) ||
                        !sellerCarteGriseValid ||
                        !sellerCerfaCessionValid ||
                        !sellerCerfaDemandeImmatValid ||
                        !sellerMandatValid ||
                        !sellerAssuranceValid ||
                        !sellerControleTechniqueValid ||
                        !buyerIdentityValid ||
                        !buyerLicenseValid ||
                        (buyerClientType === 'person' && !buyerResidencyValidForStructured) ||
                        (cobuyer === 'yes' && !coBuyerFilesValid)) && (
                        <Typography color="error" align="right">
                          Veuillez remplir tous les champs obligatoires et uploader tous les documents requis
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Box>
        </Paper>
      </Container>
      {/* Modale de confirmation */}
      <Dialog open={isConfirmModalOpen} onClose={undefined} maxWidth="lg">
        <DialogContent>
          <Typography mb={2} variant="body2">
            Êtes-vous sûr que toutes les informations sont correctes et que <b>votre dossier est complet</b> ?
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }} mb={2}>
            Merci de bien vérifier les informations saisies car toute modification pourra entrainer des frais.
          </Typography>
          <Divider
            variant="fullWidth"
            component="hr"
            style={{
              marginLeft: 24,
              marginTop: 15,
              marginBottom: 15,
              backgroundColor: theme.palette.saffron.main,
              height: '2px',
              width: 'calc(100% - 24px)',
              border: 0,
            }}
          />
          <Typography variant="body2">
            Afin de pouvoir traiter vos dossier au plus vite, et que vos client reçoivent leur carte grise le plus
            rapidement possible,{' '}
            <b>
              veuillez prendre connaisssance de la liste des pièces obligatoires demandées par l'ANTS pour pouvoir
              émettre des déclarations de cession et Changement de titulaire :
            </b>
            <List>
              <ListItem>
                📄 Le certificat d'immatriculation anciennement{' '}
                <b style={{ marginLeft: '4px' }}>carte grise avec la mention cédée le .../../... et la signature</b>{' '}
                (Titulaire et cotitulaire)
              </ListItem>
              <ListItem>
                🆔 <b style={{ marginRight: '4px' }}>Pièce d'identité</b> du nouveau titulaire en cours de validité
              </ListItem>
              <ListItem>
                🆔 <b style={{ marginRight: '4px' }}>Pièce d'identité</b> du co-titulaire en cours de validité, si il y
                a un co-titulaire
              </ListItem>
              <ListItem>
                🚗 <b style={{ marginRight: '4px' }}>Permis de conduire</b> pour les personnes physiques
              </ListItem>
              <ListItem>
                🏠 <b style={{ marginRight: '4px' }}>Justificatif de domicile</b> de moins de 6 mois
              </ListItem>
              <ListItem>
                🛡️ <b style={{ marginRight: '4px' }}>Attestation d'assurance</b> ou carte verte (indiquant
                immatriculation du véhicule assuré). // Les devis ne sont pas acceptés
              </ListItem>
              <ListItem>
                🔧 <b style={{ marginRight: '4px' }}>Contrôle technique</b> favorable de moins de 6 mois ou{' '}
                <b>Contrôle technique non-favorable</b> de moins de 2 mois (si le véhicule à plus de 4 ans)
              </ListItem>
              <ListItem>
                📝 <b style={{ marginRight: '4px' }}>Cerfa</b> de déclaration de cession complété par le vendeur et
                acquéreur
                <span style={{ margin: '0 4px' }}>
                  <Link
                    href="https://immatriculation.ants.gouv.fr/files/1e4341b6-4695-4f74-a51a-5ef962fba392/ef4ec059-07a7-4cc7-804f-e16944afeb5e/87e1c701-91dd-49de-86ed-52b7cb406dd0/cerfa_15776-01.pdf"
                    target="_blank"
                  >
                    (Cerfa 15776*01)
                  </Link>
                </span>
                original
              </ListItem>
              <ListItem>
                🖋️ <b style={{ marginRight: '4px' }}>Mandat</b> à un professionnel pour effectuer les formalités
                d'immatriculation auprès du ministère de l'intérieur
                <span style={{ marginLeft: '4px' }}>
                  <Link href="https://www.service-public.fr/simulateur/calcul/13757" target="_blank">
                    (Cerfa 13757*03)
                  </Link>
                  complété au nom de Skipcar
                </span>
              </ListItem>
              <ListItem>
                📑 <b style={{ marginRight: '4px' }}>Cerfa</b> de demande d'immatriculation d'un véhicule{' '}
                <span style={{ marginLeft: '4px' }}>
                  <Link
                    href="https://immatriculation.ants.gouv.fr/files/1e4341b6-4695-4f74-a51a-5ef962fba392/ef4ec059-07a7-4cc7-804f-e16944afeb5e/0b6477d5-86c9-43bb-bf65-08ade3d1ca37/cerfa_13750-05.pdf"
                    target="_blank"
                  >
                    (Cerfa 13750*05)
                  </Link>
                </span>
              </ListItem>
              <ListItem>
                📃 Le récépissé de la précédente déclaration d'achat uniquement, si véhicule acheté à un autre
                professionnel
              </ListItem>
            </List>
          </Typography>
        </DialogContent>
        <DialogActions style={{ marginLeft: 'auto', marginRight: '0' }}>
          <Button
            className="secondaryDealActionButton"
            onClick={() => {
              setIsConfirmModalOpen(false);
              setHasSubmissionStarted(false); // Remettre à zéro si on annule
            }}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button className="dealActionButton" onClick={handleFinalConfirm} disabled={isLoading}>
            {isLoading && <CircularProgress sx={{ marginRight: 2 }} size={24} />}
            Confirmer
          </Button>
        </DialogActions>
        {isLoading && (
          <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" align="center" mt={1}>
              {currentStep}...
            </Typography>
            <Typography variant="body2" align="center" mt={1} color="textSecondary">
              Le processus peut parfois être long, merci de patienter.
            </Typography>
          </Box>
        )}
      </Dialog>

      <ErrorMessageSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        handleClose={() => setSnackbarOpen(false)}
        severity={severity}
      />
      {taxData && agencyData && (
        <TaxModal
          open={modalOpen}
          handleClose={() => {
            setModalOpen(false);
            setProgress(0);
            setCurrentStep('');
            setHasSubmissionStarted(false); // Remettre à zéro après fermeture de TaxModal
          }}
          taxData={taxData}
          agencyData={agencyData}
          regPlate={regPlateValue}
          vin={vinValue}
          numFormule={numFormuleValue}
          registrationId={registrationId}
        />
      )}

      <FileCarouselDialog
        allFiles={allCarouselFiles}
        initialIndex={carouselIndex}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
      />
    </>
  );
};

export default CGForm;
